import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { io as socketIOClient } from 'socket.io-client';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';
import './PendientesPages.css';
import { baseColumnDefs, parseFechaPedido, promesaRowClassRules } from './baseDatosColumns';
import { API_BASE_URL, SOCKET_URL } from './config';
import { getUserRoles } from './userRoles';

ModuleRegistry.registerModules([AllCommunityModule]);

const COLUMN_VISIBILITY_STORAGE_KEY = 'baseDatosColumnVisibility';
const normalizeLocalidad = (value) => (value ?? '').toString().trim().toLowerCase();
const normalizeKeyPart = (value) => (value ?? '').toString().trim().toUpperCase();
const buildSiniestroItemKey = (siniestro, item) => {
  const siniestroKey = normalizeKeyPart(siniestro);
  const itemKey = normalizeKeyPart(item);
  if (!siniestroKey && !itemKey) return '';
  return `${siniestroKey}|||${itemKey}`;
};
const buildPedidoItemKey = (pedido, item) => {
  const pedidoKey = normalizeKeyPart(pedido);
  const itemKey = normalizeKeyPart(item);
  if (!pedidoKey && !itemKey) return '';
  return `${pedidoKey}|||${itemKey}`;
};
const getRowMasterBaseValue = (row) => {
  if (!row || typeof row !== 'object') return '';
  const cotizacion = row.COTIZACION ?? row.cotizacion ?? row.Cotizacion ?? '';
  if (normalizeKeyPart(cotizacion)) return cotizacion;
  return row.PEDIDO ?? row.pedido ?? row.Pedido ?? '';
};
const buildRowMasterKey = (row) => {
  if (!row || typeof row !== 'object') return '';
  const item = row.ITEM ?? row.item ?? row.Item ?? '';
  return buildPedidoItemKey(getRowMasterBaseValue(row), item);
};
const ROW_FILTER_EXIT_ANIMATION_MS = 260;
const getGridRowIdentityKey = (row) => {
  if (!row || typeof row !== 'object') return '';
  if (row.id != null) return String(row.id);
  return buildRowMasterKey(row);
};
const extractSiniestro = (row) => row?.SINIESTRO ?? row?.siniestro ?? row?.Siniestro ?? '';
const extractItem = (row) => row?.ITEM ?? row?.item ?? row?.Item ?? '';
const normalizeStatus = (value) => (
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);

  const isMultiasistenciaAseguradora = (value) => normalizeStatus(value).includes('multiasistencia');
const isQualitasXnuupAseguradora = (value) => { const aseguradora = normalizeStatus(value); return aseguradora.includes('qualitas') || aseguradora.includes('xnuup') || aseguradora.includes('xnuub'); };

const normalizeCancelacionFilterValue = (value) => (
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);
const ALLOWED_STATUS = new Set(
  ['Entregado', 'En Procesamiento', 'Facturado', 'Aguardando Confirmacion', 'Nuevo', 'Liberado', 'Asignado'].map(normalizeStatus)
);
const ADJUNTOS_MAX_PER_REGISTRO = 10;
const ADJUNTOS_ACCEPTED_EXTENSIONS = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt';
const ADJUNTOS_IMAGE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp'
]);
const ESTATUS2_ALWAYS_ALLOWED = new Set(['cambio', 'correo', 'en transito']);
const isValeManualRow = (row) => normalizeStatus(row?.ESTATUS) === 'vale manual';
const isValeManualSalidaModuloRow = (row) => isValeManualRow(row) && ['facturar', 'cancelado'].includes(normalizeStatus(row?.ESTATUS2));
const ROLE_RESTRICTED_FIELDS = new Set(['ESTATUS_LOCAL', 'ESTATUS_FORANEO', 'ESTATUS2']);
const UNRESTRICTED_EDITABLE_FIELDS = new Set(['LOCALIDAD']);
const CAPTURA_EDITABLE_FIELDS = new Set(['CHOFER', 'FECHA_CANCELACION_DE_PIEZA']);
const EDITABLE_FIELDS = new Set([
  'NUEVO_ESTATUS',
  ...ROLE_RESTRICTED_FIELDS,
  ...UNRESTRICTED_EDITABLE_FIELDS,
  ...CAPTURA_EDITABLE_FIELDS,
  'DIAS_ENTREGADO1',
  'PEDIDO',
  'BACK_ORDER'
]);
const ESTATUS_CANCELACION_OPTIONS = Object.freeze([
  'RECUPERAR PIEZA',
  'RECUPERAR EFECTIVO',
  'RECUPERADO',
  'NO SURTIDO',
  'DEVOLUCION A PROVEEDOR',
  ' '
]);
const ESTATUS2_OPTIONS = Object.freeze([
  '',
  'ACUSE CORREO',
  'ACUSE PENDIENTE',
  'CAMBIO',
  'CAMBIO OK',
  'CANCELADO',
  'CORREO',
  'FACTURADO',
  'FACTURAR',
  'MENSAJE',
  'PAGADO',
  'PEDIR REASIGNACION',
  'PENDIENTE',
  'RECUPERAR EFECTIVO',
  'RUTA',
  'TERMINADO'
]);
const ESTATUS2_SELECT_FIELD_OPTIONS = Object.freeze({
  ESTATUS2: ESTATUS2_OPTIONS,
  DIAS_ENTREGADO1: ESTATUS_CANCELACION_OPTIONS
});
const FACTURACION_SYNC_STATUS_MAP = new Map([
  ['facturar', 'FACTURAR'],
  ['facturado', 'FACTURADO'],
  ['pagado', 'PAGADO']
]);
const getFacturacionSyncStatusUpdate = (value) => {
  const normalized = normalizeStatus(value);
  if (normalized === '') {
    return { shouldSync: true, value: '' };
  }
  const mapped = FACTURACION_SYNC_STATUS_MAP.get(normalized);
  if (!mapped) {
    return { shouldSync: false, value: '' };
  }
  return { shouldSync: true, value: mapped };
};
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});
const SELECTION_FIELD = '__select__';
const MS_PER_DAY = 86400 * 1000;
const SELF_UPDATE_SUPPRESSION_MS = 1800;
const ESTATUS_CANCELACION_PAUSA = new Set(['RECUPERADO', 'NO SURTIDO']);

const normalizeCancelacionStatus = (value) => {
  if (value == null) return '';
  return String(value).trim().toUpperCase();
};

const isBackOrderEmptyValue = (value) => {
  const text = (value ?? '').toString().trim();
  return text === '' || text === '0' || text === '0.0';
};

const hasCancelacionDateValue = (value) => String(value ?? '').trim() !== '';

const preserveBackOrderTimestamp = (currentValue, nextValue, rowData) => {
  const hasCancelDate = hasCancelacionDateValue(rowData?.FECHA_CANCELACION_DE_PIEZA);
  const estatus2IsCambio = normalizeStatus(rowData?.ESTATUS2).includes('cambio');
  if (hasCancelDate || !estatus2IsCambio) {
    return nextValue;
  }
  if (!isBackOrderEmptyValue(currentValue) && isBackOrderEmptyValue(nextValue)) {
    return currentValue;
  }
  return nextValue;
};

const mergeRowsKeepingBackOrder = (previousRows, nextRows) => {
  if (!Array.isArray(nextRows)) return [];
  if (!Array.isArray(previousRows) || !previousRows.length) return nextRows;

  const previousById = new Map();
  previousRows.forEach((row) => {
    const id = row?.id;
    if (id == null) return;
    previousById.set(String(id), row);
  });

  return nextRows.map((row) => {
    const id = row?.id;
    if (id == null) return row;
    const prev = previousById.get(String(id));
    if (!prev) return row;

    const mergedBackOrder = preserveBackOrderTimestamp(prev.BACK_ORDER, row.BACK_ORDER, row);
    const currentBackOrder = row?.BACK_ORDER == null ? '' : String(row.BACK_ORDER);
    const nextBackOrder = mergedBackOrder == null ? '' : String(mergedBackOrder);
    if (currentBackOrder === nextBackOrder) return row;

    return { ...row, BACK_ORDER: mergedBackOrder };
  });
};

const isRecoveringCancelacionStatus = (normalizedStatus) => normalizedStatus.includes('RECUPERAR');

const buildDiasSinRecuperarLabel = (backOrderValue, fechaCancelacionValue, nowDate = new Date()) => {
  const backOrderDate = parseRowDateValue(backOrderValue);
  const fechaCancelacionDate = parseRowDateValue(fechaCancelacionValue);
  const referenceDate = (backOrderDate && !Number.isNaN(backOrderDate.getTime()))
    ? backOrderDate
    : ((fechaCancelacionDate && !Number.isNaN(fechaCancelacionDate.getTime())) ? fechaCancelacionDate : null);
  if (!referenceDate) return '';
  const startReferenceDate = new Date(referenceDate);
  startReferenceDate.setHours(0, 0, 0, 0);
  const startNow = new Date(nowDate);
  startNow.setHours(0, 0, 0, 0);
  const diffMs = startNow.getTime() - startReferenceDate.getTime();
  if (Number.isNaN(diffMs)) return '';
  const diffDays = Math.max(0, Math.floor(diffMs / MS_PER_DAY));
  return `${diffDays} dias`;
};

const formatFileSizeLabel = (bytes) => {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric < 0) return '';
  if (numeric >= 1024 * 1024) {
    return `${(numeric / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (numeric >= 1024) {
    return `${(numeric / 1024).toFixed(1)} KB`;
  }
  return `${numeric} B`;
};

const formatAdjuntoFecha = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const isImageMimeType = (mimeType) => {
  if (!mimeType || typeof mimeType !== 'string') return false;
  return ADJUNTOS_IMAGE_MIME_TYPES.has(mimeType.toLowerCase());
};

const normalizeSearchValue = (value) => {
  const normalized = (value ?? '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  return {
    spaced: normalized,
    compact: normalized.replace(/\s+/g, '')
  };
};

const applyTextFilter = (rows, text, mode) => {
  if (!text) return rows;

  const query = normalizeSearchValue(text);

  if (!query.spaced && !query.compact) return rows;

  const matches = (value) => {
    if (value == null) return false;

    const candidate = normalizeSearchValue(value);

    if (mode === 'exact') {
      return candidate.spaced === query.spaced
        || candidate.compact === query.compact;
    }

    return candidate.spaced.includes(query.spaced)
      || candidate.compact.includes(query.compact);
  };

  return rows.filter(row => {
    const values = Object.values(row);

    if (mode === 'not_contains') {
      return values.every(val => !matches(val));
    }

    return values.some(val => matches(val));
  });
};

const parseRowDateValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const fromSerial = new Date(Math.round((value - 25569) * 86400 * 1000));
    if (!Number.isNaN(fromSerial.getTime())) return fromSerial;
  }
  if (value === null || typeof value === 'undefined') return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const yearRaw = Number(match[3]);
    const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const hour = Number(match[4] || 0);
    const minute = Number(match[5] || 0);
    const second = Number(match[6] || 0);

    const parsed = new Date(year, month - 1, day, hour, minute, second);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const direct = new Date(raw);
  if (!Number.isNaN(direct.getTime())) return direct;

  return null;
};

const parseDateInputStart = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseDateInputEnd = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999);
};

const applyDateRangeFilter = (rows, field, startDateInput, endDateInput) => {
  if (!Array.isArray(rows)) return [];
  if (!field) return rows;
  const start = parseDateInputStart(startDateInput);
  const end = parseDateInputEnd(endDateInput);
  if (!start && !end) return rows;

  return rows.filter((row) => {
    const parsed = parseRowDateValue(row?.[field]);
    if (!parsed) return false;
    if (start && parsed < start) return false;
    if (end && parsed > end) return false;
    return true;
  });
};

export const defaultSortRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => parseFechaPedido(b?.FECHA_PEDIDO) - parseFechaPedido(a?.FECHA_PEDIDO));
};

const filterLocalRows = (rows) => {
  if (!Array.isArray(rows)) return [];
  const filtered = rows.filter(row => /multiasistencia|qualitas|xnuup|xnuub/.test(String(row?.ASEGURADORA ?? '').toLowerCase()) === false && normalizeLocalidad(row?.LOCALIDAD) === 'local');
  return defaultSortRows(filtered);
};

const applyColumnFieldOrder = (columns, order = EMPTY_ARRAY) => {
  if (!Array.isArray(columns) || !Array.isArray(order) || order.length === 0) return columns;
  const wanted = order.filter(Boolean);
  const wantedSet = new Set(wanted);
  const picked = wanted.map(field => columns.find(col => col.field === field)).filter(Boolean);
  if (!picked.length) return columns;
  const firstIndex = columns.findIndex(col => col.field === wanted[0]);
  if (firstIndex < 0) return columns;
  const rest = columns.filter(col => !wantedSet.has(col.field));
  const insertIndex = columns.slice(0, firstIndex).filter(col => !wantedSet.has(col.field)).length;
  return [...rest.slice(0, insertIndex), ...picked, ...rest.slice(insertIndex)];
};

const PendientesPage = ({
  pageTitle = 'Pendientes',
  pageSubtitle = '',
  filterBaseRows = defaultSortRows,
  exportFilePrefix = 'pendientes',
  exportSheetName,
  columnVisibilityStorageKey,
  applyStatusGate = true,
  dateRangeField = '',
  dateRangeLabel = 'Fecha',
  dateRangeFieldOptions = EMPTY_ARRAY,
  dateRangeStartLabel = '',
  dateRangeEndLabel = '',
  initialDateRangeStart = '',
  initialDateRangeEnd = '',
  initialDateRangeField = '',
  statusQuickTabs = [],
  statusQuickTabField = '',
  initialStatusQuickTab = '',
  dateRangeFieldByStatusQuickTab = EMPTY_OBJECT,
  dateRangeFieldLockedByStatusQuickTab = EMPTY_OBJECT,
  additionalEditableFields = EMPTY_ARRAY,
  fieldEditRoles = EMPTY_OBJECT,
  fieldEditRowPredicates = EMPTY_OBJECT,
  selectFieldOptions = EMPTY_OBJECT,
  getAdditionalRowClass = null,
  additionalRowClassRules = null,
  enableCancelacionCounter = false,
  highlightDuplicateRows = true,
  enablePromesaRowClassRules = true,
  columnFieldOrder = EMPTY_ARRAY,
  hiddenColumnFields = EMPTY_ARRAY,
  customToolbarActions = EMPTY_ARRAY,
} = {}) => {
  const pageTitleText = pageTitle || 'Pendientes';
  const pageSubtitleText = pageSubtitle || '';
  const hiddenColumnFieldSet = useMemo(
    () => new Set((Array.isArray(hiddenColumnFields) ? hiddenColumnFields : EMPTY_ARRAY).filter(Boolean)),
    [hiddenColumnFields]
  );
  const resolvedSheetName = exportSheetName || pageTitleText;
  const dateStartText = dateRangeStartLabel || `Inicio de ${dateRangeLabel.toLowerCase()}`;
  const dateEndText = dateRangeEndLabel || `Fin de ${dateRangeLabel.toLowerCase()}`;
  const resolvedDateRangeOptions = useMemo(() => {
    const options = Array.isArray(dateRangeFieldOptions)
      ? dateRangeFieldOptions
        .map((option) => {
          if (!option || typeof option !== 'object') return null;
          const value = option.value == null ? '' : String(option.value).trim();
          if (!value) return null;
          return {
            value,
            label: option.label == null ? '' : String(option.label).trim(),
            startLabel: option.startLabel == null ? '' : String(option.startLabel).trim(),
            endLabel: option.endLabel == null ? '' : String(option.endLabel).trim()
          };
        })
        .filter(Boolean)
      : [];

    if (options.length) return options;
    if (!dateRangeField) return [];
    return [{
      value: dateRangeField,
      label: dateRangeLabel,
      startLabel: dateStartText,
      endLabel: dateEndText
    }];
  }, [dateEndText, dateRangeField, dateRangeFieldOptions, dateRangeLabel, dateStartText]);
  const filterFn = useMemo(() => (typeof filterBaseRows === 'function' ? filterBaseRows : defaultSortRows), [filterBaseRows]);
  const visibilityStorageKey = columnVisibilityStorageKey || COLUMN_VISIBILITY_STORAGE_KEY;
  const gridRef = useRef(null);
  const revertingRef = useRef(false);
  const [baseDataRaw, setBaseDataRaw] = useState([]);
  const [nuevoEstatusData, setNuevoEstatusData] = useState([]);
  const [globalDuplicateKeys, setGlobalDuplicateKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchMode, setSearchMode] = useState('contains');
  const [secondarySearchText, setSecondarySearchText] = useState('');
  const [secondarySearchMode, setSecondarySearchMode] = useState('contains');
  const [tertiarySearchText, setTertiarySearchText] = useState('');
  const [tertiarySearchMode, setTertiarySearchMode] = useState('contains');
  const [dateRangeStart, setDateRangeStart] = useState(initialDateRangeStart);
  const [dateRangeEnd, setDateRangeEnd] = useState(initialDateRangeEnd);
  const [activeDateRangeField, setActiveDateRangeField] = useState(() => {
    const preferredField = String(initialDateRangeField || '').trim();
    if (preferredField) return preferredField;
    if (resolvedDateRangeOptions.length) return resolvedDateRangeOptions[0].value;
    return dateRangeField || '';
  });
  const [activeStatusQuickTab, setActiveStatusQuickTab] = useState(initialStatusQuickTab);
  const [showColumnList, setShowColumnList] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [cellInspector, setCellInspector] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [adjuntosLoading, setAdjuntosLoading] = useState(false);
  const [adjuntosError, setAdjuntosError] = useState('');
  const [adjuntosUploading, setAdjuntosUploading] = useState(false);
  const [adjuntosReloadKey, setAdjuntosReloadKey] = useState(0);
  const [previewAdjunto, setPreviewAdjunto] = useState(null);
  const [counterNow, setCounterNow] = useState(() => Date.now());
  const undoStackRef = useRef([]);
  const pendingUndoRef = useRef(null);
  const suppressHistoryRef = useRef(false);
  const selfUpdateTimestampRef = useRef(0);
  const forcedBackOrderByIdRef = useRef(new Map());
  const previousFilteredRowsRef = useRef([]);
  const exitingRowTimersRef = useRef(new Map());
  const [exitingRowsMap, setExitingRowsMap] = useState(() => new Map());

  const markSelfUpdate = useCallback(() => {
    selfUpdateTimestampRef.current = Date.now();
  }, []);

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('usuario') || '{}');
    } catch (err) {
      console.warn('No se pudo leer el usuario desde localStorage:', err);
      return {};
    }
  }, []);
  const userRoles = useMemo(() => getUserRoles(usuario), [usuario]);
  const esChoferBackOrder = ['chofer', 'choferes'].includes(String(usuario?.role || '').trim().toLowerCase());
  const usuarioNombre = useMemo(() => {
    const raw = usuario?.username || usuario?.usuario || usuario?.nombreUsuario || usuario?.nombre || '';
    return raw ? String(raw).trim() : '';
  }, [usuario]);
  const esAdministrador = userRoles.includes('administrador') || userRoles.includes('administradores');
  const puedeEditarPedidoMultiasistencia = userRoles.includes('supervisor') || userRoles.includes('captura');
  const canEditEstatusCancelacion = (
    esAdministrador
    || userRoles.includes('supervisor')
    || userRoles.includes('cancelaciones')
    || userRoles.includes('cancelacion')
  );
  const puedeEditar = userRoles.includes('supervisor') || userRoles.includes('seguimientos');
  const additionalEditableFieldSet = useMemo(() => new Set(additionalEditableFields), [additionalEditableFields]);
  const editableFields = useMemo(() => new Set([...EDITABLE_FIELDS, ...additionalEditableFieldSet]), [additionalEditableFieldSet]);
  const getFieldSelectOptions = useCallback((field) => {
    if (!field) return null;
    const options = selectFieldOptions?.[field];
    return Array.isArray(options) && options.length ? options : null;
  }, [selectFieldOptions]);

  const canEditFieldForRow = useCallback((field, rowData) => {
    const rowPredicate = fieldEditRowPredicates?.[field];
    if (typeof rowPredicate !== 'function') return true;
    try {
      return Boolean(rowPredicate(rowData || {}, { userRoles }));
    } catch (error) {
      console.warn(`No se pudo evaluar la regla de edición para ${field}:`, error);
      return false;
    }
  }, [fieldEditRowPredicates, userRoles]);

  useEffect(() => {
    if (!resolvedDateRangeOptions.length) {
      if (activeDateRangeField !== '') {
        setActiveDateRangeField('');
      }
      return;
    }
    const hasActiveField = resolvedDateRangeOptions.some((option) => option.value === activeDateRangeField);
    if (!hasActiveField) {
      setActiveDateRangeField(resolvedDateRangeOptions[0].value);
    }
  }, [activeDateRangeField, resolvedDateRangeOptions]);

  const activeDateRangeOption = useMemo(() => {
    if (!resolvedDateRangeOptions.length) return null;
    return resolvedDateRangeOptions.find((option) => option.value === activeDateRangeField) || resolvedDateRangeOptions[0];
  }, [activeDateRangeField, resolvedDateRangeOptions]);

  const activeDateRangeLabel = activeDateRangeOption?.label || dateRangeLabel;
  const activeDateStartText = activeDateRangeOption?.startLabel
    || dateRangeStartLabel
    || `Inicio de ${String(activeDateRangeLabel || 'fecha').toLowerCase()}`;
  const activeDateEndText = activeDateRangeOption?.endLabel
    || dateRangeEndLabel
    || `Fin de ${String(activeDateRangeLabel || 'fecha').toLowerCase()}`;

  const mappedDateRangeFieldForActiveQuickTab = useMemo(() => {
    if (!activeStatusQuickTab) return '';
    const mappedFieldRaw = dateRangeFieldByStatusQuickTab?.[activeStatusQuickTab];
    const mappedField = mappedFieldRaw == null ? '' : String(mappedFieldRaw).trim();
    if (!mappedField) return '';
    const exists = resolvedDateRangeOptions.some((option) => option.value === mappedField);
    return exists ? mappedField : '';
  }, [activeStatusQuickTab, dateRangeFieldByStatusQuickTab, resolvedDateRangeOptions]);

  const isDateRangeFieldLockedForActiveQuickTab = useMemo(() => (
    Boolean(activeStatusQuickTab && dateRangeFieldLockedByStatusQuickTab?.[activeStatusQuickTab])
  ), [activeStatusQuickTab, dateRangeFieldLockedByStatusQuickTab]);

  useEffect(() => {
    if (!mappedDateRangeFieldForActiveQuickTab) return;
    setActiveDateRangeField((currentField) => (
      currentField === mappedDateRangeFieldForActiveQuickTab
        ? currentField
        : mappedDateRangeFieldForActiveQuickTab
    ));
  }, [mappedDateRangeFieldForActiveQuickTab]);

  useEffect(() => {
    if (!isDateRangeFieldLockedForActiveQuickTab) return;
    if (!mappedDateRangeFieldForActiveQuickTab) return;
    if (activeDateRangeField !== mappedDateRangeFieldForActiveQuickTab) {
      setActiveDateRangeField(mappedDateRangeFieldForActiveQuickTab);
    }
  }, [activeDateRangeField, isDateRangeFieldLockedForActiveQuickTab, mappedDateRangeFieldForActiveQuickTab]);

  useEffect(() => {
    if (!enableCancelacionCounter) return undefined;
    const timerId = window.setInterval(() => {
      setCounterNow(Date.now());
    }, 60 * 1000);
    return () => window.clearInterval(timerId);
  }, [enableCancelacionCounter]);

  const inspectorInputRef = useRef(null);
  const adjuntosFileInputRef = useRef(null);
  const inspectorSuppressCommitRef = useRef(false);
  const inspectorFocusTrackerRef = useRef({ field: null, rowKey: null });
  const inspectorStateRef = useRef(null);

  const pushUndoEntry = useCallback((entry) => {
    const stack = undoStackRef.current;
    stack.push(entry);
    while (stack.length > 5) {
      stack.shift();
    }
    setCanUndo(stack.length > 0);
  }, []);

  const restoreUndoEntry = useCallback(() => {
    const entry = pendingUndoRef.current;
    if (!entry) {
      suppressHistoryRef.current = false;
      return;
    }
    const stack = undoStackRef.current;
    stack.push(entry);
    while (stack.length > 5) {
      stack.shift();
    }
    pendingUndoRef.current = null;
    suppressHistoryRef.current = false;
    setCanUndo(stack.length > 0);
  }, []);

  const updateRowValueInState = useCallback((rowId, rowKey, field, rawValue) => {
    if (!field) return;
    const normalizedValue = rawValue == null ? '' : String(rawValue);
    setBaseDataRaw(prev => {
      if (!Array.isArray(prev) || !prev.length) return prev;

      let targetIndex = -1;
      if (rowId != null) {
        const rowIdStr = String(rowId);
        targetIndex = prev.findIndex(row => row && row.id != null && String(row.id) === rowIdStr);
      }

      if (targetIndex < 0 && rowKey) {
        targetIndex = prev.findIndex(row => {
          if (!row) return false;
          return buildRowMasterKey(row) === rowKey;
        });
      }

      if (targetIndex < 0) return prev;

      const currentRow = prev[targetIndex];
      if (!currentRow) return prev;
      // const currentRaw = currentRow[field];
      // const currentValue = currentRaw == null ? '' : String(currentRaw);
      // Siempre crear nueva referencia de fila y array, aunque el valor sea igual
      const next = [...prev];
      next[targetIndex] = { ...currentRow, [field]: normalizedValue };
      return next;
    });
  }, []);

  const applyForcedBackOrderRows = useCallback((rows) => {
    if (!Array.isArray(rows) || !rows.length) return rows;
    return rows.map((row) => {
      const rowId = row?.id;
      if (rowId == null) return row;
      const rowIdKey = String(rowId);
      const forced = forcedBackOrderByIdRef.current.get(rowIdKey);
      const hasCancelDate = hasCancelacionDateValue(row?.FECHA_CANCELACION_DE_PIEZA);
      const estatus2IsCambio = normalizeStatus(row?.ESTATUS2).includes('cambio');
      const estatus2IsRecoverCash = normalizeStatus(row?.ESTATUS2) === 'recuperar efectivo';
      const currentBackOrder = row?.BACK_ORDER == null ? '' : String(row.BACK_ORDER);
      const aseg = normalizeStatus(row?.ASEGURADORA);
      const proteger = aseg.includes('multiasistencia')
        || aseg.includes('qualitas')
        || aseg.includes('xnuup')
        || aseg.includes('xnuub');

      if (proteger) return row;

      if (hasCancelDate) {
        const normalizedCancelacionStatus = normalizeCancelacionStatus(row?.DIAS_ENTREGADO1);
        const shouldKeepBackOrder = normalizedCancelacionStatus === 'RECUPERAR EFECTIVO' || estatus2IsCambio || estatus2IsRecoverCash;
        const needsBackOrderCleanup = !shouldKeepBackOrder && !isBackOrderEmptyValue(currentBackOrder);

        if (forced) {
          forcedBackOrderByIdRef.current.delete(rowIdKey);
        }

        if (!needsBackOrderCleanup) {
          return row;
        }

        return {
          ...row,
          BACK_ORDER: ''
        };
      }

      if (!estatus2IsCambio) {
        if (forced && !isBackOrderEmptyValue(currentBackOrder)) {
          forcedBackOrderByIdRef.current.delete(rowIdKey);
        }
        return row;
      }

      if (forced && isBackOrderEmptyValue(currentBackOrder)) {
        return { ...row, BACK_ORDER: forced };
      }

      if (!isBackOrderEmptyValue(currentBackOrder)) {
        forcedBackOrderByIdRef.current.set(rowIdKey, currentBackOrder);
      }

      return row;
    });
  }, []);

  useEffect(() => {
    inspectorStateRef.current = cellInspector;
  }, [cellInspector]);

  const selectedRegistroId = useMemo(() => {
    const rowData = cellInspector?.rowNode?.data;
    if (!rowData) return null;
    const rawId = rowData.id ?? rowData.ID;
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [cellInspector]);

  const adjuntosSlotsDisponibles = selectedRegistroId
    ? Math.max(ADJUNTOS_MAX_PER_REGISTRO - adjuntos.length, 0)
    : 0;

  useEffect(() => {
    if (!selectedRegistroId) {
      setAdjuntos([]);
      setAdjuntosError('');
      setAdjuntosLoading(false);
      return;
    }
    const controller = new AbortController();
    setAdjuntosLoading(true);
    setAdjuntosError('');
    fetch(`${API_BASE_URL}/api/basedatos/${selectedRegistroId}/adjuntos`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.mensaje || 'No se pudieron cargar los adjuntos.');
        }
        return response.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setAdjuntos(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('No se pudieron cargar los adjuntos:', err);
        setAdjuntos([]);
        setAdjuntosError(err.message || 'No se pudieron cargar los adjuntos.');
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setAdjuntosLoading(false);
        }
      });
    return () => controller.abort();
  }, [selectedRegistroId, adjuntosReloadKey]);

  const handleAdjuntosReload = useCallback(() => {
    if (!selectedRegistroId) return;
    setAdjuntosReloadKey(value => value + 1);
  }, [selectedRegistroId]);

  const handleAdjuntosUploadClick = useCallback(() => {
    if (!selectedRegistroId) {
      alert('Selecciona un registro para adjuntar archivos.');
      return;
    }
    adjuntosFileInputRef.current?.click();
  }, [selectedRegistroId]);

  const handleAdjuntosFileChange = useCallback(async (event) => {
    if (!selectedRegistroId) return;
    const input = event.target;
    const files = Array.from(input?.files || []);
    if (!files.length) return;
    const disponibles = Math.max(ADJUNTOS_MAX_PER_REGISTRO - adjuntos.length, 0);
    if (disponibles <= 0) {
      alert('Este registro ya tiene el máximo de adjuntos permitidos.');
      if (input) input.value = '';
      return;
    }
    if (files.length > disponibles) {
      alert(`Solo puedes subir ${disponibles} archivo(s) más para este registro.`);
      if (input) input.value = '';
      return;
    }
    setAdjuntosUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('archivos', file));
      if (usuarioNombre) {
        formData.append('subidoPor', usuarioNombre);
      }
      const response = await fetch(`${API_BASE_URL}/api/basedatos/${selectedRegistroId}/adjuntos`, {
        method: 'POST',
        body: formData
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudieron subir los archivos.');
      }
      const nuevos = Array.isArray(payload?.adjuntos) ? payload.adjuntos : [];
      if (nuevos.length) {
        setAdjuntos(prev => [...nuevos, ...prev]);
      }
    } catch (err) {
      console.error('No se pudieron subir los adjuntos:', err);
      alert(err.message || 'No se pudieron subir los archivos.');
    } finally {
      setAdjuntosUploading(false);
      if (input) {
        input.value = '';
      }
    }
  }, [selectedRegistroId, adjuntos.length, usuarioNombre]);

  const handleAdjuntoDelete = useCallback(async (adjuntoId) => {
    const numericId = Number(adjuntoId);
    if (!Number.isInteger(numericId)) return;
    const confirmed = window.confirm('¿Deseas eliminar este archivo?');
    if (!confirmed) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/basedatos/adjuntos/${numericId}`, {
        method: 'DELETE'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.ok === false) {
        throw new Error(payload?.mensaje || 'No se pudo eliminar el adjunto.');
      }
      setAdjuntos(prev => prev.filter(item => Number(item?.id) !== numericId));
    } catch (err) {
      console.error('No se pudo eliminar el adjunto:', err);
      alert(err.message || 'No se pudo eliminar el adjunto.');
    }
  }, []);

  const buildAdjuntoViewUrl = useCallback((adjunto) => (
    `${API_BASE_URL}/api/basedatos/adjuntos/${adjunto?.id}/view`
  ), []);

  const handleAdjuntoPreview = useCallback((adjunto) => {
    if (!adjunto) return;
    if (isImageMimeType(adjunto.mime_type)) {
      setPreviewAdjunto(adjunto);
    } else {
      window.open(buildAdjuntoViewUrl(adjunto), '_blank', 'noopener');
    }
  }, [buildAdjuntoViewUrl]);

  const handleClosePreview = useCallback(() => {
    setPreviewAdjunto(null);
  }, []);

  const canEditField = useCallback((field, rowData = null) => {
    if (!field) return false;
    if (field === 'ESTATUS_PORTAL_Q') {
      return isQualitasXnuupAseguradora(rowData?.ASEGURADORA) && ['supervisor','supervisores','facturacion','cancelacion','cancelaciones','captura'].some((role) => userRoles.includes(role));
    }
    if (field === 'FECHA_CANCELACION_DE_PIEZA' && isQualitasXnuupAseguradora(rowData?.ASEGURADORA) && ['supervisor','supervisores','facturacion','cancelacion','cancelaciones','captura'].some((role) => userRoles.includes(role))) return true;
    if (field === "NUEVO_ESTATUS" && (isMultiasistenciaAseguradora(rowData?.ASEGURADORA) || isValeManualRow(rowData))) return true;
    if (field === "ESTATUS2" && isMultiasistenciaAseguradora(rowData?.ASEGURADORA)) return true;
    if (!canEditFieldForRow(field, rowData)) return false;
    if (field === 'PEDIDO') {
      return puedeEditarPedidoMultiasistencia && (isMultiasistenciaAseguradora(rowData?.ASEGURADORA) || isQualitasXnuupAseguradora(rowData?.ASEGURADORA));
    }
    if (field === 'DIAS_ENTREGADO1') return canEditEstatusCancelacion;
    if (field === 'BACK_ORDER' && isQualitasXnuupAseguradora(rowData?.ASEGURADORA)) return esChoferBackOrder === false;
    const configuredRoles = fieldEditRoles?.[field];
    if (Array.isArray(configuredRoles) && configuredRoles.length) {
      const allowedRoles = configuredRoles.map((item) => String(item).trim().toLowerCase());
      return allowedRoles.some((item) => userRoles.includes(item));
    }
    if (ROLE_RESTRICTED_FIELDS.has(field)) return puedeEditar;
    if (UNRESTRICTED_EDITABLE_FIELDS.has(field)) return true;
    if (CAPTURA_EDITABLE_FIELDS.has(field)) return true;
    if (additionalEditableFieldSet.has(field)) return false;
    return false;
  }, [additionalEditableFieldSet, canEditEstatusCancelacion, canEditFieldForRow, esChoferBackOrder, fieldEditRoles, puedeEditar, puedeEditarPedidoMultiasistencia, userRoles]);

  const columnDefs = useMemo(() => {
    const selectionColumn = {
      headerName: '',
      field: SELECTION_FIELD,
      width: 36,
      pinned: 'left',
      lockPinned: true,
      suppressMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      cellClass: 'selection-checkbox-cell'
    };

    const dataColumns = applyColumnFieldOrder(
      baseColumnDefs.filter(col => col.field !== 'checked' && !hiddenColumnFieldSet.has(col.field)),
      columnFieldOrder
    )
      .map(col => {
        if (!col.field) return { ...col };
        const fieldName = col.field;
        const isEditableField = editableFields.has(fieldName);
        const selectOptions = getFieldSelectOptions(fieldName);
        return {
          ...col,
          editable: isEditableField
            ? (params) => canEditField(fieldName, params?.data)
            : false,
          cellEditor: isEditableField
            ? (selectOptions ? 'agSelectCellEditor' : (col.cellEditor || 'agTextCellEditor'))
            : col.cellEditor,
          cellEditorParams: isEditableField && selectOptions
            ? { values: selectOptions }
            : col.cellEditorParams,
        };
      });

    return [selectionColumn, ...dataColumns];
  }, [canEditField, editableFields, getFieldSelectOptions, columnFieldOrder, hiddenColumnFieldSet]);

  const baseColumnFields = useMemo(
    () => columnDefs
      .map(col => col.field)
      .filter(field => field && field !== SELECTION_FIELD),
    [columnDefs]
  );

  const columnLabels = useMemo(() => {
    const labels = {};
    columnDefs.forEach(col => {
      if (col.field && col.field !== SELECTION_FIELD) {
        labels[col.field] = col.headerName || col.field;
      }
    });
    return labels;
  }, [columnDefs]);

  const defaultColumnVisibility = useMemo(() => {
    const defaults = {};
    baseColumnFields.forEach(field => {
      defaults[field] = true;
    });
    return defaults;
  }, [baseColumnFields]);

  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnVisibilityLoaded, setColumnVisibilityLoaded] = useState(false);

  const nuevoEstatusLookup = useMemo(() => {
    const map = new Map();
    nuevoEstatusData.forEach(row => {
      if (!row) return;
      const key = buildRowMasterKey(row);
      if (!key || map.has(key)) return;
      const estatus = row.ESTATUS;
      if (typeof estatus === 'undefined' || estatus === null) return;
      const normalized = typeof estatus === 'string' ? estatus : String(estatus);
      map.set(key, normalized);
    });
    return map;
  }, [nuevoEstatusData]);

  const enrichedRows = useMemo(() => {
    if (!Array.isArray(baseDataRaw) || baseDataRaw.length === 0) return baseDataRaw;
    return baseDataRaw.map(row => {
      if (!row) return row;
      if (isMultiasistenciaAseguradora(row?.ASEGURADORA)) return row;
      const key = buildRowMasterKey(row);
      if (!key || !nuevoEstatusLookup.has(key)) return row;
      const overlayRaw = nuevoEstatusLookup.get(key);
      const overlay = overlayRaw == null ? '' : String(overlayRaw);
      if (overlay.trim() === '') return row;
      const currentRaw = row.NUEVO_ESTATUS == null ? '' : String(row.NUEVO_ESTATUS);
      if (currentRaw.trim() !== '') return row;
      if (currentRaw === overlay) return row;
      return { ...row, NUEVO_ESTATUS: overlay };
    });
  }, [baseDataRaw, nuevoEstatusLookup]);

  const sectionFilteredRows = useMemo(() => {
    if (!Array.isArray(enrichedRows)) return [];
    try {
      const nextRows = filterFn(enrichedRows);
      return Array.isArray(nextRows) ? nextRows : [];
    } catch (error) {
      console.warn('No se pudo aplicar el filtro de sección en tiempo real:', error);
      return enrichedRows;
    }
  }, [enrichedRows, filterFn]);

  const permittedRows = useMemo(() => (
    Array.isArray(sectionFilteredRows)
      ? (
        applyStatusGate
          ? sectionFilteredRows.filter(row => {
            if (isValeManualSalidaModuloRow(row)) return false;
            const normalizedNuevo = normalizeStatus(row.NUEVO_ESTATUS);
            const normalizedEstatus2 = normalizeStatus(row.ESTATUS2);
            if (ESTATUS2_ALWAYS_ALLOWED.has(normalizedEstatus2) || normalizedNuevo === 'en transito') return true;
            return normalizedNuevo === '' || ALLOWED_STATUS.has(normalizedNuevo);
          })
          : sectionFilteredRows
      )
      : []
  ), [applyStatusGate, sectionFilteredRows]);

  const rowsWithCancelacionCounter = useMemo(() => {
    if (!enableCancelacionCounter || !Array.isArray(permittedRows)) return permittedRows;
    const nowDate = new Date(counterNow);
    return permittedRows.map((row) => {
      if (!row) return row;
      const statusCancelacion = normalizeCancelacionStatus(row.DIAS_ENTREGADO1);
      const currentDiasRaw = row.DIAS_ENTREGADO2 == null ? '' : String(row.DIAS_ENTREGADO2);
      const computedDias = buildDiasSinRecuperarLabel(row.BACK_ORDER, row.FECHA_CANCELACION_DE_PIEZA, nowDate);
      let nextDias = currentDiasRaw;

      if (ESTATUS_CANCELACION_PAUSA.has(statusCancelacion)) {
        nextDias = '0 dias';
      } else if (isRecoveringCancelacionStatus(statusCancelacion)) {
        nextDias = computedDias;
      } else {
        nextDias = '';
      }

      if (currentDiasRaw !== nextDias) {
        return { ...row, DIAS_ENTREGADO2: nextDias };
      }
      return row;
    });
  }, [counterNow, enableCancelacionCounter, permittedRows]);

  const rowsAfterQuickTabFilter = useMemo(() => {
    if (!Array.isArray(rowsWithCancelacionCounter)) return [];
    if (!Array.isArray(statusQuickTabs) || !statusQuickTabs.length) return rowsWithCancelacionCounter;
    if (!activeStatusQuickTab) return rowsWithCancelacionCounter;

    const activeTab = statusQuickTabs.find((tab) => tab?.id === activeStatusQuickTab);
    if (!activeTab) return rowsWithCancelacionCounter;
    if (!statusQuickTabField) return rowsWithCancelacionCounter;

    if (typeof activeTab.filterFn === 'function') {
      return rowsWithCancelacionCounter.filter((row) => activeTab.filterFn(row, statusQuickTabField));
    }

    const expected = normalizeCancelacionFilterValue(activeTab.value);
    return rowsWithCancelacionCounter.filter((row) => {
      const normalized = normalizeCancelacionFilterValue(row?.[statusQuickTabField]);
      if (!expected) return true;
      if (activeTab.match === 'equals') return normalized === expected;
      return normalized.includes(expected);
    });
  }, [activeStatusQuickTab, rowsWithCancelacionCounter, statusQuickTabField, statusQuickTabs]);

  const filteredData = useMemo(() => {
    const byDate = applyDateRangeFilter(rowsAfterQuickTabFilter, activeDateRangeField, dateRangeStart, dateRangeEnd);
    const primary = applyTextFilter(byDate, searchText, searchMode);
    const secondary = applyTextFilter(primary, secondarySearchText, secondarySearchMode);
    return applyTextFilter(secondary, tertiarySearchText, tertiarySearchMode);
  }, [activeDateRangeField, dateRangeEnd, dateRangeStart, rowsAfterQuickTabFilter, searchMode, searchText, secondarySearchMode, secondarySearchText, tertiarySearchMode, tertiarySearchText]);

  const filteredDataIdSet = useMemo(() => {
    const next = new Set();
    filteredData.forEach((row) => {
      const key = getGridRowIdentityKey(row);
      if (key) next.add(key);
    });
    return next;
  }, [filteredData]);

  const leavingRowsNow = useMemo(() => {
    const previousRows = previousFilteredRowsRef.current;
    if (!Array.isArray(previousRows) || !previousRows.length) return [];
    const leaving = [];
    previousRows.forEach((row) => {
      const key = getGridRowIdentityKey(row);
      if (!key || filteredDataIdSet.has(key)) return;
      leaving.push({ key, row });
    });
    return leaving;
  }, [filteredDataIdSet]);

  useEffect(() => {
    const timers = exitingRowTimersRef.current;

    if (filteredDataIdSet.size) {
      filteredDataIdSet.forEach((key) => {
        const timeoutId = timers.get(key);
        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timers.delete(key);
        }
      });

      setExitingRowsMap((prev) => {
        if (!(prev instanceof Map) || !prev.size) return prev;
        let changed = false;
        const next = new Map(prev);
        filteredDataIdSet.forEach((key) => {
          if (next.delete(key)) {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    if (!leavingRowsNow.length) return;

    setExitingRowsMap((prev) => {
      const next = new Map(prev);
      leavingRowsNow.forEach(({ key, row }) => {
        if (!next.has(key)) {
          next.set(key, row);
        }
        if (!timers.has(key)) {
          const timeoutId = window.setTimeout(() => {
            exitingRowTimersRef.current.delete(key);
            setExitingRowsMap((current) => {
              if (!(current instanceof Map) || !current.has(key)) return current;
              const updated = new Map(current);
              updated.delete(key);
              return updated;
            });
          }, ROW_FILTER_EXIT_ANIMATION_MS);
          timers.set(key, timeoutId);
        }
      });
      return next;
    });
  }, [filteredDataIdSet, leavingRowsNow]);

  useEffect(() => {
    previousFilteredRowsRef.current = filteredData;
  }, [filteredData]);

  useEffect(() => () => {
    exitingRowTimersRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    exitingRowTimersRef.current.clear();
  }, []);

  const animatedFilteredData = useMemo(() => {
    const exitingEntries = [];
    const seen = new Set();

    leavingRowsNow.forEach(({ key, row }) => {
      if (!key || seen.has(key) || filteredDataIdSet.has(key)) return;
      seen.add(key);
      exitingEntries.push({ key, row });
    });

    if (exitingRowsMap instanceof Map && exitingRowsMap.size) {
      exitingRowsMap.forEach((row, key) => {
        if (!key || seen.has(key) || filteredDataIdSet.has(key)) return;
        seen.add(key);
        exitingEntries.push({ key, row });
      });
    }

    if (!exitingEntries.length) return filteredData;

    const markedRows = exitingEntries.map(({ row }) => ({
      ...(row || {}),
      __ravExitAnimation: true
    }));

    return [...filteredData, ...markedRows];
  }, [exitingRowsMap, filteredData, filteredDataIdSet, leavingRowsNow]);

  const cancelacionCounters = useMemo(() => {
    const counters = { sinRecuperar: 0, recuperado: 0 };
    if (!Array.isArray(filteredData) || !filteredData.length) return counters;

    filteredData.forEach((row) => {
      const status = normalizeCancelacionFilterValue(row?.DIAS_ENTREGADO1);
      if (status === 'recuperar pieza' || status === 'recuperar efectivo') {
        counters.sinRecuperar += 1;
      }
      if (status === 'recuperado' || status === 'no surtido') {
        counters.recuperado += 1;
      }
    });

    return counters;
  }, [filteredData]);

  const globalDuplicateSet = useMemo(() => {
    const set = new Set();
    globalDuplicateKeys.forEach(key => {
      if (key) set.add(key);
    });
    return set;
  }, [globalDuplicateKeys]);

  const localDuplicateSet = useMemo(() => {
    if (!Array.isArray(baseDataRaw) || baseDataRaw.length === 0) return new Set();
    const counts = new Map();
    baseDataRaw.forEach(row => {
      const key = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const duplicates = new Set();
    counts.forEach((count, key) => {
      if (count > 1) duplicates.add(key);
    });
    return duplicates;
  }, [baseDataRaw]);

  const combinedDuplicateSet = useMemo(() => {
    const union = new Set();
    globalDuplicateSet.forEach(key => union.add(key));
    localDuplicateSet.forEach(key => union.add(key));
    return union;
  }, [globalDuplicateSet, localDuplicateSet]);

  const getRowClass = useCallback((params) => {
    const data = params?.data;
    if (!data) return '';
    const classNames = [];
    if (data.__ravExitAnimation) {
      classNames.push('row-filter-exit');
    }
    const key = buildSiniestroItemKey(extractSiniestro(data), extractItem(data));
    if (highlightDuplicateRows && key && combinedDuplicateSet.has(key)) {
      classNames.push('row-texto-rojo');
    }
    if (typeof getAdditionalRowClass === 'function') {
      const customClass = getAdditionalRowClass(params);
      if (customClass && String(customClass).trim()) {
        classNames.push(String(customClass).trim());
      }
    }
    return classNames.join(' ');
  }, [combinedDuplicateSet, getAdditionalRowClass, highlightDuplicateRows]);

  const mergedRowClassRules = useMemo(() => {
    const rules = {};
    if (enablePromesaRowClassRules && promesaRowClassRules) {
      Object.assign(rules, promesaRowClassRules);
    }
    if (additionalRowClassRules && typeof additionalRowClassRules === 'object') {
      Object.assign(rules, additionalRowClassRules);
    }
    return Object.keys(rules).length ? rules : undefined;
  }, [additionalRowClassRules, enablePromesaRowClassRules]);

  const cargarDatos = useCallback(() => {
    fetch(`${API_BASE_URL}/api/basedatos/obtener?full=1`)
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) {
          setBaseDataRaw([]);
          setGlobalDuplicateKeys([]);
          return;
        }
        const counts = new Map();
        data.forEach(row => {
          const key = buildSiniestroItemKey(extractSiniestro(row), extractItem(row));
          if (!key) return;
          counts.set(key, (counts.get(key) || 0) + 1);
        });
        const duplicates = [];
        counts.forEach((count, key) => {
          if (count > 1) duplicates.push(key);
        });
        setGlobalDuplicateKeys(duplicates);
        const filteredRows = filterFn(data);
        setBaseDataRaw((prev) => applyForcedBackOrderRows(mergeRowsKeepingBackOrder(prev, Array.isArray(filteredRows) ? filteredRows : [])));
      })
      .catch(() => {
        setBaseDataRaw([]);
        setGlobalDuplicateKeys([]);
      });
  }, [applyForcedBackOrderRows, filterFn]);

  const cargarNuevoEstatus = useCallback(() => {
    fetch(`${API_BASE_URL}/api/nuevo-estatus/obtener`)
      .then(res => res.json())
      .then(payload => {
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.rows)
            ? payload.rows
            : [];
        setNuevoEstatusData(rows);
      })
      .catch(() => setNuevoEstatusData([]));
  }, []);

  const aplicarActualizacionSocket = useCallback((payload) => {
    if (!payload) return false;
    const { type, id, field, value, changes } = payload;
    if (!id) return false;

    let encontrado = false;
    setBaseDataRaw(prev => {
      if (!Array.isArray(prev) || !prev.length) return prev;
      const targetIndex = prev.findIndex(row => row && row.id != null && String(row.id) === String(id));
      if (targetIndex < 0) return prev;
      encontrado = true;
      const currentRow = prev[targetIndex];
      if (!currentRow) return prev;
      let siguiente = { ...currentRow };
      if (type === 'estatus_update_multi' && Array.isArray(changes)) {
        for (const cambio of changes) {
          if (!cambio.field) continue;
          const nextValue = cambio.value == null ? '' : String(cambio.value);
          if (cambio.field === 'BACK_ORDER') {
            const preservedValue = preserveBackOrderTimestamp(siguiente.BACK_ORDER, nextValue, siguiente);
            siguiente[cambio.field] = preservedValue == null ? '' : String(preservedValue);
          } else {
            siguiente[cambio.field] = nextValue;
          }
        }
      } else if (type === 'estatus_update' && field) {
        const nextValue = value == null ? '' : String(value);
        if (field === 'BACK_ORDER') {
          const preservedValue = preserveBackOrderTimestamp(siguiente.BACK_ORDER, nextValue, siguiente);
          siguiente[field] = preservedValue == null ? '' : String(preservedValue);
        } else {
          siguiente[field] = nextValue;
        }
      } else if (type === 'captura_cell_update' && field) {
        siguiente[field] = value == null ? '' : String(value);
      }
      const nextArr = [...prev];
      nextArr[targetIndex] = applyForcedBackOrderRows([siguiente])[0];
      return nextArr;
    });
    return encontrado;
  }, [applyForcedBackOrderRows]);

  useEffect(() => {
    cargarDatos();
    cargarNuevoEstatus();
    const socket = socketIOClient(SOCKET_URL);
    const handleExcelUpdated = (payload) => {
      if (Date.now() - selfUpdateTimestampRef.current < SELF_UPDATE_SUPPRESSION_MS) {
        return;
      }
      const manejado = aplicarActualizacionSocket(payload);
      if (!manejado) {
        cargarDatos();
      }
    };
    socket.on('excel_data_updated', handleExcelUpdated);
    socket.on('nuevo_estatus_updated', () => {
      cargarNuevoEstatus();
      cargarDatos();
    });
    return () => {
      socket.off('excel_data_updated', handleExcelUpdated);
      socket.disconnect();
    };
  }, [aplicarActualizacionSocket, cargarDatos, cargarNuevoEstatus]);

  useEffect(() => {
  const storedRaw = localStorage.getItem(visibilityStorageKey);
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        setColumnVisibility({ ...defaultColumnVisibility, ...stored });
        setColumnVisibilityLoaded(true);
        return;
      } catch (err) {
        console.warn('No se pudo leer la visibilidad guardada:', err);
      }
    }
    setColumnVisibility(defaultColumnVisibility);
    setColumnVisibilityLoaded(true);
  }, [defaultColumnVisibility, visibilityStorageKey]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    localStorage.setItem(visibilityStorageKey, JSON.stringify(columnVisibility));
  }, [columnVisibility, columnVisibilityLoaded, visibilityStorageKey]);

  useEffect(() => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const next = { ...prev };
      let changed = false;
      baseColumnFields.forEach(field => {
        if (typeof next[field] === 'undefined') {
          next[field] = true;
          changed = true;
        }
      });
      Object.keys(next).forEach(field => {
        if (!baseColumnFields.includes(field)) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [baseColumnFields, columnVisibilityLoaded]);

  const handleToggleColumnVisibility = useCallback((field) => {
    if (!columnVisibilityLoaded) return;
    setColumnVisibility(prev => {
      const currentlyVisible = prev[field] !== false;
      return { ...prev, [field]: !currentlyVisible };
    });
  }, [columnVisibilityLoaded]);

  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.api.getSelectedRows();
    setSelectedCount(selectedRows.length);
  }, []);

  const handleCustomToolbarAction = useCallback(async (action) => {
    if (!action || typeof action.onClick !== 'function') return;
    const api = gridRef.current?.api;
    const selectedRows = api?.getSelectedRows ? api.getSelectedRows() : [];

    try {
      await action.onClick({
        selectedRows,
        api,
        updateRowValueInState,
        markSelfUpdate
      });
    } catch (err) {
      console.error('No se pudo ejecutar la acción personalizada:', err);
      alert(err?.message || 'No se pudo ejecutar la acción.');
    }
  }, [markSelfUpdate, updateRowValueInState]);

  const handleCopySelectedRows = useCallback(async () => {
    if (!gridRef.current) return;
    const api = gridRef.current.api;
    const selectedRows = api.getSelectedRows();
    if (!selectedRows.length) {
      alert('Selecciona al menos un renglón para copiar.');
      return;
    }

    const visibleColumns = columnDefs.filter(col => (
      col.field &&
      col.field !== SELECTION_FIELD &&
      (columnVisibilityLoaded ? columnVisibility[col.field] !== false : true)
    ));

    if (!visibleColumns.length) {
      alert('No hay columnas visibles para copiar.');
      return;
    }

    const headers = visibleColumns.map(col => col.headerName || col.field);
    const formattingRow = headers.map(() => '---------------');

    const escapeHtml = (raw) => {
      const value = raw == null ? '' : String(raw);
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    };

    const headerHtml = headers
      .map(label => `<th style="border:1px solid #9ca3af;padding:6px 8px;background:#1f2937;color:#ffffff;font-weight:600;">${escapeHtml(label)}</th>`)
      .join('');

    const rowsHtml = selectedRows.map(row => {
      const cells = visibleColumns
        .map(col => `<td style="border:1px solid #d1d5db;padding:6px 8px;">${escapeHtml(row[col.field])}</td>`)
        .join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const htmlTable = `<!DOCTYPE html><html><body><table border="0" cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%;font-family:Arial, sans-serif;font-size:13px;"><thead><tr>${headerHtml}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;

    const plainRows = selectedRows.map(row => (
      visibleColumns
        .map(col => {
          const raw = row[col.field];
          return raw == null ? '' : String(raw);
        })
        .join(' | ')
    ));
    const plainText = [headers.join(' | '), formattingRow.join(' | '), ...plainRows].join('\n');

    let clipboardSucceeded = false;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.write) {
      try {
        const blobHtml = new Blob([htmlTable], { type: 'text/html' });
        const blobText = new Blob([plainText], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({
          'text/html': blobHtml,
          'text/plain': blobText
        });
        await navigator.clipboard.write([clipboardItem]);
        clipboardSucceeded = true;
      } catch (err) {
        try {
          await navigator.clipboard.writeText(plainText);
          clipboardSucceeded = true;
        } catch (errText) {
          console.warn('No se pudo copiar formato enriquecido:', err, errText);
        }
      }
    } else if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(plainText);
        clipboardSucceeded = true;
      } catch (err) {
        console.warn('No se pudo copiar texto plano:', err);
      }
    }

    if (!clipboardSucceeded) {
      api.copySelectedRowsToClipboard({
        processCellCallback: ({ value }) => (value == null ? '' : String(value)),
        columnKeys: visibleColumns.map(col => col.field)
      });
      clipboardSucceeded = true;
    }
  }, [columnDefs, columnVisibility, columnVisibilityLoaded]);

  const handlePrint = useCallback(() => {
    if (typeof window === 'undefined') return;

    const visibleColumns = columnDefs.filter(col => (
      col.field && col.field !== SELECTION_FIELD && (columnVisibilityLoaded ? columnVisibility[col.field] !== false : true)
    ));

    if (!visibleColumns.length) {
      alert('No hay columnas visibles para imprimir.');
      return;
    }

    const api = gridRef.current?.api;
    if (!api) {
      alert('No se pudo preparar la impresión.');
      return;
    }

    const selectedRows = api.getSelectedRows();
    if (!Array.isArray(selectedRows) || !selectedRows.length) {
      alert('Selecciona al menos un renglón para imprimir.');
      return;
    }

    const escapeHtml = (raw) => {
      const value = raw == null ? '' : String(raw);
      return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    const headerHtml = visibleColumns
      .map(col => `<th>${escapeHtml(col.headerName || col.field)}</th>`)
      .join('');

    const rowsHtml = selectedRows
      .map(row => {
        const cells = visibleColumns
          .map(col => `<td>${escapeHtml(row[col.field])}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>${pageTitleText}</title>
  <style>
    @page { size: landscape; margin: 12mm; }
    body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
    h1 { font-size: 17px; margin: 0 0 10px 0; }
    table { border-collapse: collapse; width: auto; min-width: 100%; table-layout: auto; }
    thead { display: table-header-group; }
    tbody { display: table-row-group; }
    tbody tr { page-break-inside: avoid; }
    th, td {
      border: 1px solid #333;
      padding: 3px 6px;
      font-size: 11px;
      min-width: 60px;
      word-break: break-word;
      white-space: normal;
    }
    th {
      background: #d1d5db;
      color: #111827;
      text-align: left;
      font-weight: 700;
    }
    tr:nth-child(even) { background: #f3f4f6; }
  </style>
</head>
<body style="margin:12mm;">
  <h1>${pageTitleText}</h1>
  <table>
    <thead><tr>${headerHtml}</tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
</body>
</html>`;

    try {
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow?.document;
      if (!doc) throw new Error('No se pudo crear el documento de impresión.');

      doc.open();
      doc.write(html);
      doc.close();
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();

      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    } catch (err) {
      console.error('No se pudo preparar la impresión:', err);
      alert('No se pudo preparar la impresión.');
    }
  }, [columnDefs, columnVisibility, columnVisibilityLoaded, pageTitleText]);

  const updateInspectorForNode = useCallback((node, field, updater) => {
    setCellInspector(prev => {
      if (!prev || prev.rowNode !== node || prev.field !== field) return prev;
      return updater(prev);
    });
  }, []);

  const handleCellEdit = useCallback((params) => {
    if (revertingRef.current) return;

    const skipHistory = suppressHistoryRef.current === true;

    const field = params?.colDef?.field;
    if (!field || (
      !editableFields.has(field)
      && !CAPTURA_EDITABLE_FIELDS.has(field)
    )) {
      if (skipHistory) restoreUndoEntry();
      return;
    }

    updateInspectorForNode(params.node, field, prev => ({ ...prev, isCommitting: true }));

    const fieldIsCapturaEditable = CAPTURA_EDITABLE_FIELDS.has(field) && !(field === 'FECHA_CANCELACION_DE_PIEZA' && isQualitasXnuupAseguradora(params?.data?.ASEGURADORA));
    const puedeEditarCampo = canEditField(field, params?.data);

    if (!puedeEditarCampo) {
      revertingRef.current = true;
      params.node.setDataValue(field, params.oldValue ?? '');
      revertingRef.current = false;
      updateInspectorForNode(params.node, field, prev => {
        const revertValue = params.oldValue == null ? '' : String(params.oldValue);
        return { ...prev, value: revertValue, originalValue: revertValue, isDirty: false, isCommitting: false };
      });
      if (skipHistory) restoreUndoEntry();
      return;
    }

    const rowId = params?.data?.id;
    const rowKey = buildRowMasterKey(params?.data);
    const numericId = Number(rowId);
    if (!Number.isInteger(numericId)) {
      revertingRef.current = true;
      params.node.setDataValue(field, params.oldValue ?? '');
      revertingRef.current = false;
      updateInspectorForNode(params.node, field, prev => {
        const revertValue = params.oldValue == null ? '' : String(params.oldValue);
        return { ...prev, value: revertValue, originalValue: revertValue, isDirty: false, isCommitting: false };
      });
      if (skipHistory) restoreUndoEntry();
      return;
    }

    const oldValue = params.oldValue == null ? '' : String(params.oldValue);
    const newValueRaw = params.newValue == null ? '' : params.newValue;
    let newValue = typeof newValueRaw === 'string' ? newValueRaw : String(newValueRaw);
    const fieldOptions = getFieldSelectOptions(field);
    if (fieldOptions) {
      const matchedOption = fieldOptions.find((option) => String(option).trim().toLowerCase() === newValue.trim().toLowerCase());
      if (matchedOption === undefined) {
        revertingRef.current = true;
        params.node.setDataValue(field, oldValue);
        revertingRef.current = false;
        updateRowValueInState(numericId, rowKey, field, oldValue);
        updateInspectorForNode(params.node, field, prev => ({ ...prev, value: oldValue, originalValue: oldValue, isDirty: false, isCommitting: false }));
        if (skipHistory) restoreUndoEntry();
        alert('Selecciona un valor válido de la lista.');
        return;
      }
      newValue = matchedOption;
      if (params.node?.setDataValue) {
        revertingRef.current = true;
        params.node.setDataValue(field, matchedOption);
        revertingRef.current = false;
        // Forzar refresco de la fila para actualizar color
        if (params.api && typeof params.api.refreshCells === 'function') {
          params.api.refreshCells({ rowNodes: [params.node] });
        }
      }
    }

    const syncStatusField = isMultiasistenciaAseguradora(params?.data?.ASEGURADORA)
      ? ''
      : (field === 'ESTATUS2'
          ? 'ESTATUS_DE_PAGO'
          : (field === 'ESTATUS_DE_PAGO' ? 'ESTATUS2' : ''));
    const syncStatusUpdate = syncStatusField
      ? getFacturacionSyncStatusUpdate(newValue)
      : { shouldSync: false, value: '' };
    const syncStatusValue = syncStatusUpdate.value;
    const currentSyncStatusValue = syncStatusField
      ? (params?.data?.[syncStatusField] == null ? '' : String(params.data[syncStatusField]))
      : '';
    const shouldSyncFacturacionStatus = Boolean(
      syncStatusField
      && syncStatusUpdate.shouldSync
      && normalizeStatus(currentSyncStatusValue) !== normalizeStatus(syncStatusValue)
    );
    const previousSyncStatusValue = shouldSyncFacturacionStatus ? currentSyncStatusValue : '';

    if (oldValue === newValue && !shouldSyncFacturacionStatus) {
      updateInspectorForNode(params.node, field, prev => ({ ...prev, value: newValue, originalValue: newValue, isDirty: false, isCommitting: false }));
      if (skipHistory) restoreUndoEntry();
      return;
    }

    updateRowValueInState(numericId, rowKey, field, newValue);

    if (shouldSyncFacturacionStatus) {
      if (params.node?.setDataValue) {
        revertingRef.current = true;
        params.node.setDataValue(syncStatusField, syncStatusValue);
        revertingRef.current = false;
      }
      updateRowValueInState(numericId, rowKey, syncStatusField, syncStatusValue);
    }

    const shouldPersistDiasCounter = enableCancelacionCounter && field === 'DIAS_ENTREGADO1';
    let nextDiasSinRecuperar = '';
    if (shouldPersistDiasCounter) {
      const normalizedStatus = normalizeCancelacionStatus(newValue);
      if (normalizedStatus === 'RECUPERAR EFECTIVO') {
        nextDiasSinRecuperar = '0 dias';
      } else if (ESTATUS_CANCELACION_PAUSA.has(normalizedStatus)) {
        nextDiasSinRecuperar = '0 dias';
      } else if (isRecoveringCancelacionStatus(normalizedStatus)) {
        nextDiasSinRecuperar = buildDiasSinRecuperarLabel(params?.data?.BACK_ORDER, params?.data?.FECHA_CANCELACION_DE_PIEZA, new Date());
      }
      if (params.node?.setDataValue) {
        revertingRef.current = true;
        params.node.setDataValue('DIAS_ENTREGADO2', nextDiasSinRecuperar);
        revertingRef.current = false;
      }
      updateRowValueInState(numericId, rowKey, 'DIAS_ENTREGADO2', nextDiasSinRecuperar);

      // Forzar recálculo de clase de fila en tiempo real para verde/rojo
      if (params.node?.setData) {
        revertingRef.current = true;
        params.node.setData({
          ...(params.node.data || {}),
          DIAS_ENTREGADO1: newValue,
          DIAS_ENTREGADO2: nextDiasSinRecuperar
        });
        revertingRef.current = false;
      }
    }

    const endpoint = fieldIsCapturaEditable
      ? { url: `${API_BASE_URL}/api/basedatos/captura/actualizar-celda`, method: 'POST' }
      : { url: `${API_BASE_URL}/api/basedatos/actualizar-estatus`, method: 'PUT' };

    markSelfUpdate();

    fetch(endpoint.url, {
      method: endpoint.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        shouldPersistDiasCounter
          ? {
              id: numericId,
              field: 'DIAS_ENTREGADO1',
              value: newValue,
              extraField: 'DIAS_ENTREGADO2',
              extraValue: nextDiasSinRecuperar,
              usuario: usuarioNombre
            }
          : { id: numericId, field, value: newValue, usuario: usuarioNombre }
      )
    })
      .then(res => res.json())
      .then(async (data) => {
        if (!data?.ok) {
          throw new Error(data?.mensaje || 'Error al guardar el cambio');
        }

        let effectiveUpdatedRowIds = Array.isArray(data?.updatedRowIds)
          ? data.updatedRowIds
          : [];

        const normalizedEditedStatus = normalizeStatus(newValue);
        const shouldFallbackPedidoFacturar = (
          field === 'ESTATUS2'
          && normalizedEditedStatus === 'facturar'
            && !isMultiasistenciaAseguradora(params?.data?.ASEGURADORA)
          && effectiveUpdatedRowIds.length <= 1
        );

        if (shouldFallbackPedidoFacturar) {
          const pedidoKey = normalizeKeyPart(getRowMasterBaseValue(params?.data));
          if (pedidoKey) {
            const groupedPedidoIds = new Set();
            if (params.api?.forEachNode) {
              params.api.forEachNode((rowNode) => {
                const rowPedidoKey = normalizeKeyPart(getRowMasterBaseValue(rowNode?.data));
                const rowIdValue = Number(rowNode?.data?.id);
                if (rowPedidoKey !== pedidoKey || !Number.isInteger(rowIdValue)) return;
                groupedPedidoIds.add(rowIdValue);
              });
            }

            if (Number.isInteger(numericId)) {
              groupedPedidoIds.add(numericId);
            }

            const currentUpdatedSet = new Set(
              effectiveUpdatedRowIds
                .map((item) => Number(item))
                .filter((item) => Number.isInteger(item))
            );

            const fallbackTargetIds = Array.from(groupedPedidoIds).filter((targetId) => !currentUpdatedSet.has(targetId));

            if (fallbackTargetIds.length) {
              markSelfUpdate();
              const fallbackResults = await Promise.all(
                fallbackTargetIds.map(async (targetId) => {
                  try {
                    const fallbackResponse = await fetch(`${API_BASE_URL}/api/basedatos/actualizar-estatus`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: targetId, field: 'ESTATUS2', value: newValue })
                    });
                    return await fallbackResponse.json();
                  } catch (_error) {
                    return null;
                  }
                })
              );

              fallbackResults.forEach((result, index) => {
                if (!result?.ok) return;
                const targetId = fallbackTargetIds[index];
                if (Number.isInteger(targetId)) {
                  currentUpdatedSet.add(targetId);
                }
              });

              if (currentUpdatedSet.size) {
                effectiveUpdatedRowIds = Array.from(currentUpdatedSet);
              }
            }
          }
        }

        if (Array.isArray(data?.extraChanges)) {
          data.extraChanges.forEach((change) => {
            const extraField = change?.field;
            if (!extraField) return;
            const nextValue = change?.value == null ? '' : String(change.value);
            const currentValue = params.node?.data?.[extraField] == null ? '' : String(params.node.data[extraField]);
            if (currentValue === nextValue) return;
            if (extraField === 'BACK_ORDER' && !isBackOrderEmptyValue(nextValue)) {
              forcedBackOrderByIdRef.current.set(String(numericId), nextValue);
            }
            if (params.node?.setDataValue) {
              revertingRef.current = true;
              params.node.setDataValue(extraField, nextValue);
              revertingRef.current = false;
            }
            updateRowValueInState(numericId, rowKey, extraField, nextValue);
          });
        }

        if ((field === 'NUMERO DE FACTURA' || field === 'UUID' || field === 'ESTATUS2' || field === 'ESTATUS_DE_PAGO') && Array.isArray(effectiveUpdatedRowIds) && effectiveUpdatedRowIds.length) {
          const updatedIdSet = new Set(
            effectiveUpdatedRowIds
              .map((item) => Number(item))
              .filter((item) => Number.isInteger(item))
          );
          if (updatedIdSet.size) {
            const isNumeroFacturaField = field === 'NUMERO DE FACTURA';
            const isUuidField = field === 'UUID';
            const groupedStatusValue = (
              (field === 'ESTATUS2' || field === 'ESTATUS_DE_PAGO')
                ? (normalizeStatus(newValue) === 'pagado'
                    ? 'PAGADO'
                    : (normalizeStatus(newValue) === 'facturar' ? 'FACTURAR' : ''))
                : ''
            );
            const isGroupedStatusField = groupedStatusValue !== '';
            setBaseDataRaw((prev) => {
              if (!Array.isArray(prev) || !prev.length) return prev;
              let changed = false;
              const nextRows = prev.map((row) => {
                const rowId = Number(row?.id);
                if (!Number.isInteger(rowId) || !updatedIdSet.has(rowId)) return row;
                const nextFactura = isNumeroFacturaField ? newValue : (row?.['NUMERO DE FACTURA'] == null ? '' : String(row['NUMERO DE FACTURA']));
                const nextUuid = isUuidField ? newValue : (row?.UUID == null ? '' : String(row.UUID));
                const nextEstatus2 = isNumeroFacturaField
                  ? 'FACTURADO'
                  : (isGroupedStatusField ? groupedStatusValue : (row?.ESTATUS2 == null ? '' : String(row.ESTATUS2)));
                const nextEstatusPago = isNumeroFacturaField
                  ? 'FACTURADO'
                  : (isGroupedStatusField ? groupedStatusValue : (row?.ESTATUS_DE_PAGO == null ? '' : String(row.ESTATUS_DE_PAGO)));
                const currentFactura = row?.['NUMERO DE FACTURA'] == null ? '' : String(row['NUMERO DE FACTURA']);
                const currentUuid = row?.UUID == null ? '' : String(row.UUID);
                const currentEstatus2 = row?.ESTATUS2 == null ? '' : String(row.ESTATUS2);
                const currentEstatusPago = row?.ESTATUS_DE_PAGO == null ? '' : String(row.ESTATUS_DE_PAGO);
                if (
                  currentFactura === nextFactura
                  && currentUuid === nextUuid
                  && currentEstatus2 === nextEstatus2
                  && currentEstatusPago === nextEstatusPago
                ) {
                  return row;
                }
                changed = true;
                return {
                  ...row,
                  'NUMERO DE FACTURA': nextFactura,
                  UUID: nextUuid,
                  ESTATUS2: nextEstatus2,
                  ESTATUS_DE_PAGO: nextEstatusPago
                };
              });
              return changed ? nextRows : prev;
            });
          }
        }

        updateInspectorForNode(params.node, field, prev => {
          const appliedRaw = params.node?.data?.[field];
          const appliedValue = appliedRaw == null ? '' : String(appliedRaw);
          return { ...prev, value: appliedValue, originalValue: appliedValue, isDirty: false, isCommitting: false };
        });
        const appliedRaw = params.node?.data?.[field];
        const appliedValue = appliedRaw == null ? '' : String(appliedRaw);
        updateRowValueInState(numericId, rowKey, field, appliedValue);
        if (!skipHistory) {
          pushUndoEntry({
            rowId: numericId,
            rowNodeId: params.node?.id ?? null,
            rowKey,
            field,
            previousValue: oldValue,
            newValue: appliedValue
          });
        } else {
          pendingUndoRef.current = null;
          setCanUndo(undoStackRef.current.length > 0);
        }
      })
      .catch(err => {
        console.error('No se pudo guardar el cambio:', err);
        alert('No se pudo guardar el cambio.');
        revertingRef.current = true;
        params.node.setDataValue(field, oldValue);
        revertingRef.current = false;
        updateRowValueInState(numericId, rowKey, field, oldValue);
        if (shouldSyncFacturacionStatus) {
          if (params.node?.setDataValue) {
            revertingRef.current = true;
            params.node.setDataValue(syncStatusField, previousSyncStatusValue);
            revertingRef.current = false;
          }
          updateRowValueInState(numericId, rowKey, syncStatusField, previousSyncStatusValue);
        }
        updateInspectorForNode(params.node, field, prev => ({ ...prev, value: oldValue, originalValue: oldValue, isDirty: false, isCommitting: false }));
        if (skipHistory) {
          restoreUndoEntry();
        }
      })
      .finally(() => {
        updateInspectorForNode(params.node, field, prev => ({ ...prev, isCommitting: false }));
        if (skipHistory) {
          suppressHistoryRef.current = false;
          pendingUndoRef.current = null;
          setCanUndo(undoStackRef.current.length > 0);
        }
      });
  }, [canEditField, editableFields, enableCancelacionCounter, getFieldSelectOptions, markSelfUpdate, updateInspectorForNode, pushUndoEntry, restoreUndoEntry, updateRowValueInState, usuarioNombre]);

  const filteredColumnDefs = useMemo(() => {
    if (!columnVisibilityLoaded) return columnDefs;
    return columnDefs.filter(col => {
      if (!col.field) return true;
      return columnVisibility[col.field] !== false;
    });
  }, [columnDefs, columnVisibility, columnVisibilityLoaded]);

  const updateInspectorFromParams = useCallback((params) => {
    const field = params?.colDef?.field;
    if (!field || field === SELECTION_FIELD) {
      setCellInspector(null);
      return;
    }
    const node = params?.node;
    if (!node) {
      setCellInspector(null);
      return;
    }
    const rowData = params?.data || node.data || {};
    const rawValue = rowData[field];
    const normalizedValue = rawValue == null ? '' : String(rawValue);
    const fieldOptions = getFieldSelectOptions(field);
    const inspectorValue = fieldOptions
      ? (fieldOptions.find((option) => String(option).trim().toLowerCase() === normalizedValue.trim().toLowerCase()) || '')
      : normalizedValue;
    setCellInspector({
      field,
      header: columnLabels[field] || field,
      rowNode: node,
      rowId: rowData.id ?? null,
      value: inspectorValue,
      originalValue: normalizedValue,
      editable: canEditField(field, rowData),
      isDirty: false,
      isCommitting: false
    });
  }, [canEditField, columnLabels, getFieldSelectOptions]);

  const handleCellClicked = useCallback((params) => {
    updateInspectorFromParams(params);
  }, [updateInspectorFromParams]);

  const handleCellFocused = useCallback((params) => {
    if (!params?.column) {
      setCellInspector(null);
      return;
    }
    const field = params.column.getColDef()?.field;
    if (!field) {
      setCellInspector(null);
      return;
    }
    if (field === SELECTION_FIELD) return;
    const rowIndex = params.rowIndex;
    if (typeof rowIndex !== 'number' || rowIndex < 0) {
      setCellInspector(null);
      return;
    }
    const node = params.api?.getDisplayedRowAtIndex(rowIndex);
    if (!node) {
      setCellInspector(null);
      return;
    }
    updateInspectorFromParams({
      colDef: params.column.getColDef(),
      node,
      data: node.data,
      value: node.data?.[field]
    });
  }, [updateInspectorFromParams]);

  const handleInspectorChange = useCallback((event) => {
    const nextValue = event?.target?.value ?? '';
    setCellInspector(prev => {
      if (!prev) return prev;
      if (prev.value === nextValue) return prev;
      return { ...prev, value: nextValue, isDirty: true };
    });
  }, []);

  const commitInspector = useCallback(() => {
    const current = inspectorStateRef.current;
    if (!current || !current.rowNode || !current.field) return;
    if (!current.editable) return;
    const normalizedValue = current.value == null ? '' : String(current.value);
    const previousRaw = current.rowNode.data?.[current.field];
    const previousValue = previousRaw == null ? '' : String(previousRaw);
    if (!current.isDirty && normalizedValue === current.originalValue) return;
    if (normalizedValue === previousValue) {
      setCellInspector(prev => {
        if (!prev || prev.rowNode !== current.rowNode || prev.field !== current.field) return prev;
        return { ...prev, originalValue: normalizedValue, isDirty: false, isCommitting: false };
      });
      return;
    }
    setCellInspector(prev => {
      if (!prev || prev.rowNode !== current.rowNode || prev.field !== current.field) return prev;
      return { ...prev, isCommitting: true };
    });
    current.rowNode.setDataValue(current.field, normalizedValue);
  }, []);

  const handleInspectorBlur = useCallback(() => {
    if (inspectorSuppressCommitRef.current) {
      inspectorSuppressCommitRef.current = false;
      return;
    }
    commitInspector();
  }, [commitInspector]);

  const handleInspectorKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      inspectorSuppressCommitRef.current = true;
      setTimeout(() => {
        inspectorSuppressCommitRef.current = false;
      }, 0);
      setCellInspector(null);
      inspectorFocusTrackerRef.current = { field: null, rowKey: null };
      gridRef.current?.api?.clearFocusedCell();
      return;
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      commitInspector();
    }
  }, [commitInspector]);

  const handleUndo = useCallback(() => {
    const stack = undoStackRef.current;
    if (!stack.length) return;

    if (suppressHistoryRef.current || pendingUndoRef.current) return;

    const entry = stack.pop();
    setCanUndo(stack.length > 0);

    if (!entry) return;

    const api = gridRef.current?.api;
    if (!api) {
      stack.push(entry);
      if (stack.length > 5) {
        stack.shift();
      }
      setCanUndo(stack.length > 0);
      return;
    }

    let targetNode = null;
    if (entry.rowNodeId != null) {
      targetNode = api.getRowNode(entry.rowNodeId);
    }

    if (!targetNode) {
      api.forEachNode(node => {
        if (targetNode) return;
        const dataId = node.data?.id;
        if (dataId != null && entry.rowId != null && String(dataId) === String(entry.rowId)) {
          targetNode = node;
          return;
        }
        if (entry.rowKey) {
          const key = buildRowMasterKey(node.data);
          if (key && key === entry.rowKey) {
            targetNode = node;
          }
        }
      });
    }

    if (!targetNode) {
      stack.push(entry);
      if (stack.length > 5) {
        stack.shift();
      }
      setCanUndo(stack.length > 0);
      alert('No se encontró el renglón para deshacer el cambio.');
      return;
    }

    pendingUndoRef.current = entry;
    suppressHistoryRef.current = true;
    targetNode.setDataValue(entry.field, entry.previousValue ?? '');
    setCanUndo(stack.length > 0);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.key !== 'z' && event.key !== 'Z') return;
      const target = event.target;
      const tagName = (target?.tagName || '').toLowerCase();
      const isEditableField = tagName === 'input' || tagName === 'textarea' || target?.isContentEditable;
      if (isEditableField) return;
      event.preventDefault();
      if (canUndo) {
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, canUndo]);

  const handleInspectorCancel = useCallback(() => {
    inspectorSuppressCommitRef.current = true;
    setTimeout(() => {
      inspectorSuppressCommitRef.current = false;
    }, 0);
    setCellInspector(null);
    inspectorFocusTrackerRef.current = { field: null, rowKey: null };
    gridRef.current?.api?.clearFocusedCell();
  }, []);

  const handleExportVisibleToExcel = useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;
    if (!api) {
      alert('No se pudo acceder a la tabla para exportar.');
      return;
    }

    const flattenDefs = (defs) => {
      const collected = [];
      defs?.forEach(def => {
        if (!def) return;
        if (Array.isArray(def.children) && def.children.length) {
          collected.push(...flattenDefs(def.children));
        } else {
          collected.push(def);
        }
      });
      return collected;
    };

    const columnInstances = columnApi?.getAllColumns?.() || [];
    const columnDescriptors = columnInstances.length
      ? columnInstances.map(column => ({
          column,
          colDef: column.getColDef?.() || {}
        }))
      : flattenDefs(columnDefs).map(colDef => ({ column: null, colDef }));

    const exportableDescriptors = columnDescriptors.filter(({ colDef }) => {
      if (!colDef) return false;
      if (colDef.suppressExport) return false;
      if (colDef.field === SELECTION_FIELD) return false;
      if (colDef.checkboxSelection || colDef.headerCheckboxSelection) return false;
      if (!colDef.field && typeof colDef.valueGetter !== 'function') return false;
      return true;
    });

    if (!exportableDescriptors.length) {
      alert('No hay columnas disponibles para exportar.');
      return;
    }

    const rows = [];
    api.forEachNodeAfterFilterAndSort(node => {
      const row = exportableDescriptors.map(({ column, colDef }) => {
        let rawValue;

        if (typeof colDef.valueGetter === 'function') {
          try {
            rawValue = colDef.valueGetter({
              api,
              columnApi,
              context: api?.context,
              data: node.data,
              node,
              colDef,
              column,
              getValue: (field) => (node?.data ? node.data[field] : undefined)
            });
          } catch (err) {
            console.error('No se pudo obtener el valor calculado:', err);
            rawValue = null;
          }
        } else if (colDef.field) {
          rawValue = node?.data ? node.data[colDef.field] : undefined;
        } else if (column && typeof column.getColId === 'function') {
          rawValue = node?.data ? node.data[column.getColId()] : undefined;
        } else if (colDef.colId) {
          rawValue = node?.data ? node.data[colDef.colId] : undefined;
        } else {
          rawValue = undefined;
        }

        if (rawValue === null || typeof rawValue === 'undefined') return '';
        if (rawValue instanceof Date) return rawValue.toISOString();
        if (typeof rawValue === 'object') return JSON.stringify(rawValue);
        return rawValue;
      });
      rows.push(row);
    });

    if (!rows.length) {
      alert('No hay registros para exportar.');
      return;
    }

    const headerRow = exportableDescriptors.map(({ column, colDef }, index) => {
      const displayName = column && columnApi?.getDisplayNameForColumn?.(column, 'header');
      if (displayName) return displayName;
      if (colDef.headerName) return colDef.headerName;
      if (colDef.field) return colDef.field;
      if (colDef.colId) return colDef.colId;
      if (column && typeof column.getColId === 'function') return column.getColId();
      return `Columna ${index + 1}`;
    });

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, resolvedSheetName || pageTitleText);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${exportFilePrefix}-${timestamp}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }, [columnDefs, exportFilePrefix, pageTitleText, resolvedSheetName]);

  useEffect(() => {
    if (!cellInspector || !cellInspector.editable) {
      inspectorFocusTrackerRef.current = { field: null, rowKey: null };
      return;
    }
    const field = cellInspector.field;
    const rowNode = cellInspector.rowNode;
    const rowKey = rowNode ? (rowNode.id ?? rowNode?.data?.id ?? null) : null;
    const previous = inspectorFocusTrackerRef.current;
    if (previous.field === field && previous.rowKey === rowKey) return;
    inspectorFocusTrackerRef.current = { field, rowKey };
  }, [cellInspector]);

  useEffect(() => {
    if (!cellInspector || cellInspector.isDirty || cellInspector.isCommitting) return;
    if (!cellInspector.rowNode || !cellInspector.field) return;
    const latestRaw = cellInspector.rowNode.data?.[cellInspector.field];
    const latestValue = latestRaw == null ? '' : String(latestRaw);
    if (cellInspector.value === latestValue && cellInspector.originalValue === latestValue) return;
    setCellInspector(prev => {
      if (!prev || !prev.rowNode || prev.rowNode !== cellInspector.rowNode || prev.field !== cellInspector.field) return prev;
      return { ...prev, value: latestValue, originalValue: latestValue };
    });
  }, [cellInspector, baseDataRaw]);

  const inspectorPedidoValue = cellInspector?.rowNode?.data?.PEDIDO;
  const inspectorPedidoLabel = inspectorPedidoValue == null ? '' : String(inspectorPedidoValue).trim();
  const inspectorSiniestroValue = cellInspector?.rowNode?.data?.SINIESTRO;
  const inspectorSiniestroLabel = inspectorSiniestroValue == null ? '' : String(inspectorSiniestroValue).trim();
  const inspectorItemValue = cellInspector?.rowNode?.data?.ITEM;
  const inspectorItemLabel = inspectorItemValue == null ? '' : String(inspectorItemValue).trim();
  const inspectorModeloValue = cellInspector?.rowNode?.data?.MODELO;
  const inspectorAnioValue = cellInspector?.rowNode?.data?.ANIO;
  const inspectorModeloAnioLabel = [inspectorModeloValue, inspectorAnioValue]
    .map((val) => (val == null ? '' : String(val).trim()))
    .filter(Boolean)
    .join(' ');
  const inspectorTallerValue = cellInspector?.rowNode?.data?.NOMBRE_COMERCIAL_TALLER;
  const inspectorTallerLabel = inspectorTallerValue == null ? '' : String(inspectorTallerValue).trim();
  const inspectorCiudadTallerValue = cellInspector?.rowNode?.data?.CIUDAD_TALLER;
  const inspectorCiudadTallerLabel = inspectorCiudadTallerValue == null ? '' : String(inspectorCiudadTallerValue).trim();
  const inspectorOrigenValue = cellInspector?.rowNode?.data?.ORIGEN;
  const inspectorOrigenLabel = inspectorOrigenValue == null ? '' : String(inspectorOrigenValue).trim();
  const inspectorTextareaClassName = cellInspector?.isCommitting
    ? 'inspector-textarea inspector-textarea--saving'
    : 'inspector-textarea';
  const hasRegistroSeleccionado = Boolean(selectedRegistroId);

  return (
    <div style={{ padding: '16px 16px 20px' }}>
      <h2 style={{ marginBottom: 10 }}>{pageTitleText}</h2>
      {pageSubtitleText && (
        <p style={{ marginTop: 0, marginBottom: 10, color: '#6b7280', fontSize: 13 }}>
          {pageSubtitleText}
        </p>
      )}
      <section
        style={{
          background: '#f8fafc',
          border: '1px solid #d1d5db',
          borderRadius: 10,
          padding: 10,
          marginBottom: 10,
          boxShadow: '0 3px 10px rgba(15, 23, 42, 0.05)'
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16
          }}
        >
          <div
            style={{
              flex: '1 1 420px',
              minWidth: 320,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 10,
              background: '#ffffff',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 10,
                flexWrap: 'wrap'
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                  {cellInspector?.field ? cellInspector.header : 'Inspector de celda'}
                </div>
                {cellInspector?.field ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: 14,
                        rowGap: 4,
                        fontSize: 11.5,
                        color: '#6b7280',
                        marginTop: 4
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Taller:</span>{' '}
                        {inspectorTallerLabel || 'Sin valor'}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Ciudad taller:</span>{' '}
                        {inspectorCiudadTallerLabel || 'Sin valor'}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Modelo:</span>{' '}
                        {inspectorModeloAnioLabel || 'Sin valor'}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        columnGap: 14,
                        rowGap: 4,
                        fontSize: 11.5,
                        color: '#6b7280',
                        marginTop: 2
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Pedido:</span>{' '}
                        {inspectorPedidoLabel || 'Sin valor'}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Siniestro:</span>{' '}
                        {inspectorSiniestroLabel || 'Sin valor'}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Item:</span>{' '}
                        {inspectorItemLabel || 'Sin valor'}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600, color: '#374151' }}>Origen:</span>{' '}
                        {inspectorOrigenLabel || 'Sin valor'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
                      {cellInspector.editable ? 'Los cambios se guardan al salir del campo o con Ctrl+Enter.' : 'Solo lectura.'}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 4 }}>
                    Selecciona una celda para ver su contenido.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleInspectorCancel}
                disabled={!cellInspector}
                style={{
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: '1px solid #d0d5dd',
                  background: cellInspector ? '#f9fafb' : '#f3f4f6',
                  cursor: cellInspector ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                  color: '#1f2937',
                  opacity: cellInspector ? 1 : 0.6
                }}
              >
                Limpiar selección
              </button>
            </div>
            {cellInspector ? (
              cellInspector.editable ? (
                getFieldSelectOptions(cellInspector.field) ? (
                  <select
                    className={inspectorTextareaClassName}
                    ref={inspectorInputRef}
                    value={cellInspector.value}
                    onChange={handleInspectorChange}
                    onBlur={handleInspectorBlur}
                    onKeyDown={handleInspectorKeyDown}
                    style={{ minHeight: 42 }}
                  >
                    <option value="">Selecciona una opción</option>
                    {getFieldSelectOptions(cellInspector.field).map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <textarea
                    className={inspectorTextareaClassName}
                    ref={inspectorInputRef}
                    value={cellInspector.value}
                    onChange={handleInspectorChange}
                    onBlur={handleInspectorBlur}
                    onKeyDown={handleInspectorKeyDown}
                    rows={2}
                    placeholder="Escribe el valor..."
                    style={{ minHeight: 42 }}
                  />
                )
              ) : (
                <div
                  style={{
                    minHeight: 42,
                    borderRadius: 8,
                    border: '1px solid #d8e2f3',
                    background: '#f9fafb',
                    padding: 10,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '#111827'
                  }}
                >
                  {cellInspector.value || '(vacío)'}
                </div>
              )
            ) : (
              <div
                style={{
                  minHeight: 38,
                  borderRadius: 8,
                  border: '1px dashed #d1d5db',
                  background: '#f9fafb',
                  padding: 10,
                  fontSize: 12,
                  color: '#6b7280'
                }}
              >
                No hay una celda seleccionada.
              </div>
            )}
            {cellInspector?.editable && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onMouseDown={() => { inspectorSuppressCommitRef.current = true; }}
                  onClick={() => {
                    inspectorSuppressCommitRef.current = false;
                    commitInspector();
                  }}
                  disabled={cellInspector.isCommitting}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: cellInspector.isCommitting ? '#bfdbfe' : '#3b82f6',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: cellInspector.isCommitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Guardar cambio
                </button>
              </div>
            )}
          </div>
          <div
            style={{
              flex: '1 1 420px',
              minWidth: 320,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 10,
              background: '#f9fafb',
              display: 'flex',
              flexDirection: 'column',
              gap: 8
            }}
          >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>
                Adjuntos ({hasRegistroSeleccionado ? `${adjuntos.length}/${ADJUNTOS_MAX_PER_REGISTRO}` : '—'})
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                Puedes cargar PDF, imágenes o documentos (máx. 25 MB c/u).
              </div>
              <div style={{ fontSize: 12, color: '#475467', marginTop: 2 }}>
                Espacios disponibles: {hasRegistroSeleccionado ? adjuntosSlotsDisponibles : 'Selecciona un registro'}
              </div>
              {!hasRegistroSeleccionado && (
                <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>
                  Selecciona un registro para ver y administrar sus archivos.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                ref={adjuntosFileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple
                accept={ADJUNTOS_ACCEPTED_EXTENSIONS}
                onChange={handleAdjuntosFileChange}
              />
              <button
                type="button"
                onClick={handleAdjuntosReload}
                disabled={adjuntosLoading || !hasRegistroSeleccionado}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: '1px solid #d0d5dd',
                  background: adjuntosLoading ? '#f3f4f6' : '#e0f2fe',
                  cursor: adjuntosLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  color: '#0f172a'
                }}
              >
                {adjuntosLoading ? 'Actualizando...' : 'Actualizar lista'}
              </button>
              <button
                type="button"
                onClick={handleAdjuntosUploadClick}
                disabled={!hasRegistroSeleccionado || adjuntosUploading || adjuntosSlotsDisponibles <= 0}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: '1px solid #c7d2fe',
                  background: adjuntosUploading || adjuntosSlotsDisponibles <= 0 ? '#f3f4f6' : '#dbeafe',
                  cursor: adjuntosUploading || adjuntosSlotsDisponibles <= 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  color: '#1d4ed8'
                }}
              >
                {adjuntosUploading
                  ? 'Subiendo...'
                  : !hasRegistroSeleccionado
                    ? 'Selecciona un registro'
                    : adjuntosSlotsDisponibles > 0
                      ? 'Agregar archivos'
                      : 'Máximo alcanzado'}
              </button>
            </div>
          </div>
          <div
            style={{
              border: '1px solid #d4d7dd',
              borderRadius: 10,
              padding: 10,
              background: '#ffffff',
              minHeight: 130,
              maxHeight: 190,
              overflowY: 'auto'
            }}
          >
            {!hasRegistroSeleccionado ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Selecciona un registro para consultar o subir sus archivos adjuntos.
              </div>
            ) : adjuntosError ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#b42318' }}>{adjuntosError}</span>
                <button
                  type="button"
                    onClick={handleAdjuntosReload}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 8,
                      border: '1px solid #fecaca',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                >
                  Reintentar
                </button>
              </div>
            ) : adjuntosLoading ? (
              <div style={{ fontSize: 13, color: '#475467' }}>Cargando adjuntos...</div>
            ) : adjuntos.length === 0 ? (
              <div style={{ fontSize: 13, color: '#6b7280' }}>No hay archivos adjuntos para este registro.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {adjuntos.map((archivo) => {
                  const esImagen = isImageMimeType(archivo.mime_type);
                  const viewUrl = buildAdjuntoViewUrl(archivo);
                  return (
                    <div
                      key={archivo.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: 10,
                        background: '#ffffff'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        {esImagen && (
                          <button
                            type="button"
                            onClick={() => handleAdjuntoPreview(archivo)}
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 12,
                              border: '1px solid #dbeafe',
                              overflow: 'hidden',
                              padding: 0,
                              background: '#f8fafc',
                              cursor: 'pointer'
                            }}
                          >
                            <img
                              src={`${viewUrl}?thumb=1`}
                              alt={archivo.file_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          </button>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>{archivo.file_name}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {formatFileSizeLabel(archivo.file_size)}
                            {archivo.created_at ? ` · ${formatAdjuntoFecha(archivo.created_at)}` : ''}
                            {archivo.uploaded_by ? ` · ${archivo.uploaded_by}` : ''}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleAdjuntoPreview(archivo)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 8,
                            border: '1px solid #c7d2fe',
                            background: '#eef2ff',
                            color: '#3730a3',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {esImagen ? 'Ver' : 'Abrir'}
                        </button>
                        <a
                          href={`${API_BASE_URL}/api/basedatos/adjuntos/${archivo.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            padding: '4px 10px',
                            borderRadius: 8,
                            border: '1px solid #bfdbfe',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            fontWeight: 600,
                            textDecoration: 'none'
                          }}
                        >
                          Descargar
                        </a>
                        <button
                          type="button"
                          onClick={() => handleAdjuntoDelete(archivo.id)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 8,
                            border: '1px solid #fecaca',
                            background: '#fef2f2',
                            color: '#b91c1c',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </div>
      </section>
      <section
        style={{
          marginBottom: 12,
          padding: 12,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          background: '#f8fafb',
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 10,
            marginBottom: 10
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar en la tabla..."
              style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            />
            <select
              value={searchMode}
              onChange={(e) => setSearchMode(e.target.value)}
              style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            >
              <option value="contains">Contiene</option>
              <option value="exact">Coincidencia exacta</option>
              <option value="not_contains">No contiene</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={secondarySearchText}
              onChange={(e) => setSecondarySearchText(e.target.value)}
              placeholder="Aplicar segundo filtro..."
              style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            />
            <select
              value={secondarySearchMode}
              onChange={(e) => setSecondarySearchMode(e.target.value)}
              style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            >
              <option value="contains">Contiene</option>
              <option value="exact">Coincidencia exacta</option>
              <option value="not_contains">No contiene</option>
            </select>
            {secondarySearchText && (
              <button
                type="button"
                onClick={() => {
                  setSecondarySearchText('');
                  setSecondarySearchMode('contains');
                }}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', fontWeight: 600 }}
              >
                Limpiar
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={tertiarySearchText}
              onChange={(e) => setTertiarySearchText(e.target.value)}
              placeholder="Aplicar tercer filtro..."
              style={{ flex: '1 1 160px', padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            />
            <select
              value={tertiarySearchMode}
              onChange={(e) => setTertiarySearchMode(e.target.value)}
              style={{ minWidth: 140, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
            >
              <option value="contains">Contiene</option>
              <option value="exact">Coincidencia exacta</option>
              <option value="not_contains">No contiene</option>
            </select>
            {tertiarySearchText && (
              <button
                type="button"
                onClick={() => {
                  setTertiarySearchText('');
                  setTertiarySearchMode('contains');
                }}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', fontWeight: 600 }}
              >
                Limpiar
              </button>
            )}
          </div>
          {resolvedDateRangeOptions.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#475467', fontWeight: 600, minWidth: 120 }}>
                Rango {activeDateRangeLabel}
              </span>
              {resolvedDateRangeOptions.length > 1 && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#475467', fontWeight: 600 }}>
                  <span>Filtrar por</span>
                  <select
                    value={activeDateRangeField}
                    onChange={(e) => setActiveDateRangeField(e.target.value)}
                    disabled={isDateRangeFieldLockedForActiveQuickTab}
                    title={isDateRangeFieldLockedForActiveQuickTab ? 'Este filtro está fijo para la pestaña activa.' : undefined}
                    style={{ minWidth: 180, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                  >
                    {resolvedDateRangeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label || option.value}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#475467', fontWeight: 600 }}>
                <span>{activeDateStartText}</span>
                <input
                  type="date"
                  value={dateRangeStart}
                  onChange={(e) => setDateRangeStart(e.target.value)}
                  aria-label={activeDateStartText}
                  style={{ minWidth: 170, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#475467', fontWeight: 600 }}>
                <span>{activeDateEndText}</span>
                <input
                  type="date"
                  value={dateRangeEnd}
                  onChange={(e) => setDateRangeEnd(e.target.value)}
                  aria-label={activeDateEndText}
                  style={{ minWidth: 170, padding: 6, borderRadius: 8, border: '1px solid #d0d5dd' }}
                />
              </label>
              {(dateRangeStart || dateRangeEnd) && (
                <button
                  type="button"
                  onClick={() => {
                    setDateRangeStart('');
                    setDateRangeEnd('');
                  }}
                  style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#f3f4f6', fontWeight: 600 }}
                >
                  Limpiar fechas
                </button>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center'
          }}
        >
          <button
            type="button"
            onClick={() => setShowColumnList(prev => !prev)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: showColumnList ? '#e0e7ff' : '#f3f4f6', cursor: 'pointer', fontWeight: 600 }}
          >
            {showColumnList ? 'Ocultar columnas' : 'Seleccionar columnas'}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#fef3c7', cursor: 'pointer', fontWeight: 600 }}
          >
            Imprimir
          </button>
          <button
            type="button"
            onClick={handleCopySelectedRows}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #d0d5dd', background: '#e0f2fe', cursor: 'pointer', fontWeight: 600 }}
          >
            Copiar selección
          </button>
          <button
            type="button"
            onClick={handleExportVisibleToExcel}
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid #d0d5dd',
              background: '#fef3c7',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            Exportar vista
          </button>
          <button
            type="button"
            onClick={handleUndo}
            disabled={!canUndo}
            title="Deshacer el último cambio (máximo 5)"
            style={{
              padding: '6px 14px',
              borderRadius: 8,
              border: '1px solid #d0d5dd',
              background: canUndo ? '#e0e7ff' : '#f3f4f6',
              cursor: canUndo ? 'pointer' : 'not-allowed',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <span style={{ fontSize: 16 }}>↶</span>
            Deshacer
          </button>
          {Array.isArray(customToolbarActions) && customToolbarActions.map((action) => (
            <button
              key={action.id || action.label}
              type="button"
              onClick={() => handleCustomToolbarAction(action)}
              disabled={Boolean(action.disabled)}
              style={{
                padding: '6px 14px',
                borderRadius: 8,
                border: action.border || '1px solid #d0d5dd',
                background: action.background || '#e0f2fe',
                color: action.color || '#111827',
                cursor: action.disabled ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {action.label}
            </button>
          ))}
          <span style={{ fontWeight: 600, color: '#1f2937' }}>
            Total: {filteredData.length}
          </span>
          <span style={{ fontWeight: 600, color: '#2563eb' }}>
            Seleccionadas: {selectedCount}
          </span>
          {enableCancelacionCounter && (
            <>
              <span style={{ fontWeight: 600, color: '#b45309' }}>
                Sin recuperar: {cancelacionCounters.sinRecuperar}
              </span>
              <span style={{ fontWeight: 600, color: '#047857' }}>
                Recuperado/No surtido: {cancelacionCounters.recuperado}
              </span>
            </>
          )}
        </div>
        {Array.isArray(statusQuickTabs) && statusQuickTabs.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            {statusQuickTabs.map((tab) => {
              const isActive = tab?.id === activeStatusQuickTab;
              return (
                <button
                  key={tab?.id || String(tab?.label || '')}
                  type="button"
                  onClick={() => setActiveStatusQuickTab(tab?.id || '')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: isActive ? '1px solid #1d4ed8' : '1px solid #d0d5dd',
                    background: isActive ? '#dbeafe' : '#f8fafc',
                    color: isActive ? '#1e3a8a' : '#1f2937',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {tab?.label || ''}
                </button>
              );
            })}
          </div>
        )}
      </section>
      {showColumnList && (
        <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, border: '1px solid #e5e7eb', background: '#f9fafb', display: 'flex', flexWrap: 'wrap', gap: 10, maxHeight: 220, overflowY: 'auto' }}>
          {baseColumnFields.map(field => (
            <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 200, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={columnVisibility[field] !== false}
                onChange={() => handleToggleColumnVisibility(field)}
              />
              <span>{columnLabels[field] || field}</span>
            </label>
          ))}
        </div>
      )}
      <div
        className="ag-theme-alpine"
        style={{ height: 420, width: '100%', borderRadius: 12, overflow: 'hidden' }}
      >
        <AgGridReact
          ref={gridRef}
          columnDefs={filteredColumnDefs}
          rowData={animatedFilteredData}
          getRowId={(params) => getGridRowIdentityKey(params?.data || {})}
          getRowClass={getRowClass}
          domLayout="normal"
          rowSelection="multiple"
          suppressMovableColumns={true}
          enableBrowserTooltips={true}
          enableCellTextSelection={true}
          suppressRowClickSelection={true}
          rowMultiSelectWithClick={false}
          deltaRowDataMode={true}
          defaultColDef={{
            resizable: true,
            sortable: true,
            filter: false,
            minWidth: 60,
            editable: false
          }}
          headerHeight={32}
          rowHeight={28}
          enterMovesDown={false}
          enterMovesDownAfterEdit={false}
          singleClickEdit={true}
          stopEditingWhenCellsLoseFocus={true}
          onCellValueChanged={handleCellEdit}
          onSelectionChanged={handleSelectionChanged}
          onCellClicked={handleCellClicked}
          onCellFocused={handleCellFocused}
          rowClassRules={mergedRowClassRules}
        />
      </div>
      {previewAdjunto && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={handleClosePreview}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0f172a',
              padding: 16,
              borderRadius: 12,
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewAdjunto.original_name || previewAdjunto.file_name || 'Archivo adjunto'}
              </div>
              <button
                onClick={handleClosePreview}
                style={{
                  background: '#f87171',
                  border: 'none',
                  color: '#fff',
                  fontWeight: 600,
                  padding: '6px 14px',
                  borderRadius: 999,
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </button>
            </div>
            <div
              style={{
                background: '#020617',
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 300,
                minHeight: 300,
                maxHeight: '80vh'
              }}
            >
              <img
                src={buildAdjuntoViewUrl(previewAdjunto)}
                alt={previewAdjunto.original_name || previewAdjunto.file_name || 'Vista previa'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '75vh',
                  objectFit: 'contain',
                  borderRadius: 6,
                  boxShadow: '0 0 30px rgba(15,23,42,0.6)'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const basePageDefaults = {
  pageTitle: 'Pendientes',
  pageSubtitle: '',
  filterBaseRows: defaultSortRows,
  exportFilePrefix: 'pendientes',
  exportSheetName: 'Pendientes',
  columnVisibilityStorageKey: COLUMN_VISIBILITY_STORAGE_KEY
};

const localPageConfig = {
  ...basePageDefaults,
  pageTitle: 'INPART LOCAL',
  pageSubtitle: 'Registros correspondientes a INPART LOCAL.',
  filterBaseRows: filterLocalRows,
  exportFilePrefix: 'pendientes-local',
  exportSheetName: 'INPART LOCAL',
  columnVisibilityStorageKey: 'baseDatosColumnVisibility-local',
  hiddenColumnFields: ['ESTATUS3'],
  additionalEditableFields: ['OBSERVACION_CHOFER'],
  fieldEditRoles: {
    OBSERVACION_CHOFER: ['chofer', 'supervisor']
  },
  selectFieldOptions: ESTATUS2_SELECT_FIELD_OPTIONS
};

const PendientesLocalPage = (props) => (
  <PendientesPage {...localPageConfig} {...props} />
);

export const createPendientesPage = (config = {}) => (props) => (
  <PendientesPage {...basePageDefaults} {...config} {...props} />
);

export { ESTATUS2_SELECT_FIELD_OPTIONS };

export default PendientesLocalPage;
